import '../style.css';
import { apiFetch as baseApiFetch, setApiEnvironment, getCurrentEnvironment, API_ENVIRONMENTS } from '../utils/apiClient.js';
import logger from '../utils/logger.js';
import { escapeHtml } from '../utils/html.js';
import { createRequestDetailsPanel } from './RequestDetailsPanel.js';
import { createSupportDetailsPanel } from './SupportDetailsPanel.js';
import { createErrorDetailsPanel } from './ErrorDetailsPanel.js';
import { renderEmptyStateHTML, EmptyStatePresets } from './EmptyState.js';
import { createDeleteConfirmModal } from './ui/DeleteConfirmModal.js';
import { pill, toneFor, templateIcon, renderPageHeader } from './adminUi.js';

const log = logger.withContext('Admin');
const REQUEST_STATUSES = [
    { value: 'pending', label: 'En attente' },
    { value: 'in_progress', label: 'En cours' },
    { value: 'completed', label: 'Terminées' },
];

export function createAdminComponent({ authFetch, getCurrentUser } = {}) {
    const PAGE_SIZE = 10;

    const container = document.createElement('div');
    container.className =
        'component admin-component scenario-component font-sans h-full flex antialiased overflow-hidden bg-[var(--bg-primary)] text-[color:var(--text-primary)]';
    const currentEnv = getCurrentEnvironment();
    const isLocalHost =
        typeof window !== 'undefined' &&
        ['localhost', '127.0.0.1', '0.0.0.0'].includes(window.location?.hostname || '');

    container.innerHTML = `
        <div class="admin-layout" style="display: flex; width: 100%; height: 100%; overflow: hidden; position: relative; background-color: var(--bg-primary);">
            <aside class="admin-sidebar">
                <div class="admin-sidebar-header">
                    <div class="ad-brand">
                        <img src="/images/logo/AppIcon.png" alt="" class="ad-brand-icon" width="28" height="28" />
                        <span class="ad-brand-name">Launcher</span>
                        <span class="ad-brand-pill">Admin</span>
                    </div>
                </div>
                <div class="admin-env-selector" ${currentEnv !== 'auto' ? `data-env="${currentEnv}"` : ''}>
                    <span class="admin-env-indicator"></span>
                    <label for="admin-env-select" class="admin-env-label">
                        <span class="material-symbols-outlined">dns</span>
                        <span>Environnement</span>
                    </label>
                    <select id="admin-env-select" class="admin-env-select">
                        <option value="auto" ${currentEnv === 'auto' ? 'selected' : ''}>Auto</option>
                        <option value="local" ${currentEnv === 'local' ? 'selected' : ''}>Local</option>
                        <option value="staging" ${currentEnv === 'staging' ? 'selected' : ''}>Staging</option>
                        <option value="production" ${currentEnv === 'production' ? 'selected' : ''} ${isLocalHost ? 'disabled' : ''}>${isLocalHost ? 'Production (indisponible en local)' : 'Production'}</option>
                    </select>
                </div>
                <nav class="admin-sidebar-nav">
                    <button class="admin-nav-item active" data-tab="overview">
                        <span class="material-symbols-outlined">space_dashboard</span>
                        <span>Tableau de bord</span>
                    </button>
                    <div class="ad-nav-group">Comptes</div>
                    <button class="admin-nav-item" data-tab="users">
                        <span class="material-symbols-outlined">group</span>
                        <span>Utilisateurs</span>
                    </button>
                    <button class="admin-nav-item" data-tab="requests">
                        <span class="material-symbols-outlined">inbox</span>
                        <span>Demandes</span>
                    </button>
                    <button class="admin-nav-item" data-tab="support">
                        <span class="material-symbols-outlined">support_agent</span>
                        <span>Support</span>
                    </button>
                    <div class="ad-nav-group">Suivi technique</div>
                    <button class="admin-nav-item" data-tab="executions">
                        <span class="material-symbols-outlined">play_circle</span>
                        <span>Exécutions</span>
                    </button>
                    <button class="admin-nav-item" data-tab="events">
                        <span class="material-symbols-outlined">history</span>
                        <span>Journal d'activité</span>
                    </button>
                    <button class="admin-nav-item" data-tab="errors">
                        <span class="material-symbols-outlined">error</span>
                        <span>Erreurs</span>
                        <span class="admin-nav-badge" style="display: none;"></span>
                    </button>
                    <div class="ad-nav-group">Emails</div>
                    <button class="admin-nav-item" data-tab="emails">
                        <span class="material-symbols-outlined">mail</span>
                        <span>Historique</span>
                    </button>
                    <button class="admin-nav-item" data-tab="templates">
                        <span class="material-symbols-outlined">drafts</span>
                        <span>Modèles</span>
                    </button>
                    <button class="admin-nav-item" data-tab="lifecycle">
                        <span class="material-symbols-outlined">route</span>
                        <span>Parcours</span>
                    </button>
                </nav>
                <div class="admin-sidebar-footer"></div>
            </aside>
            <div class="admin-main">
                <div class="component-content admin-content" style="flex: 1; overflow-y: auto; position: relative; scroll-behavior: smooth;">
                    <p class="admin-empty">Chargement des données...</p>
                </div>
            </div>
        </div>
    `;

    let usersSearchTimeout = null;
    let errorSearchTimeout = null;
    let emailsSearchTimeout = null;
    let eventsUserSearchTimeout = null;
    let eventsUsersList = [];
    let eventsUsersLoading = false;
    // Champ (id + curseur) qui avait le focus avant un render et qu'il faut restaurer
    // dès qu'il réapparaît (il peut disparaître le temps d'un état "Chargement...").
    let pendingFocus = null;
    // Listener document de fermeture des suggestions du formulaire d'envoi (remplacé à chaque render)
    let templateSuggestionsOutsideHandler = null;

    const state = {
        loading: true,
        error: null,
        overview: null,
        overviewLoading: true,
        overviewError: null,
        users: [],
        usersPage: 1,
        usersPageSize: 10,
        usersTotal: 0,
        usersTotalPages: 1,
        usersLoading: false,
        usersError: null,
        usersSearch: '',
        requests: [],
        requestsPage: 1,
        requestsPageSize: 10,
        requestsTotal: 0,
        requestsTotalPages: 1,
        requestsLoading: false,
        requestsError: null,
        filterType: '',
        filterStatus: '',
        currentTab: 'overview',
        events: [],
        eventsPage: 1,
        eventsPageSize: 10,
        eventsTotal: 0,
        eventsTotalPages: 1,
        eventsLoading: false,
        eventsError: null,
        eventFilterType: '',
        eventFilterUser: '',
        eventFilterUserSearch: '',
        executions: [],
        executionsPage: 1,
        executionsPageSize: 10,
        executionsTotal: 0,
        executionsTotalPages: 1,
        executionsLoading: false,
        executionsError: null,
        executionFilterStatus: '',
        errors: [],
        errorsPage: 1,
        errorsPageSize: 10,
        errorsTotal: 0,
        errorsTotalPages: 1,
        errorsLoading: false,
        errorsError: null,
        errorsStats: null,
        errorFilterSource: '',
        errorFilterLevel: '',
        errorFilterComponent: '',
        errorFilterResolved: '',
        errorSearch: '',
        supportContacts: [],
        supportPage: 1,
        supportPageSize: 10,
        supportTotal: 0,
        supportTotalPages: 1,
        supportLoading: false,
        supportError: null,
        supportFilterStatus: '',
        emails: [],
        emailsPage: 1,
        emailsPageSize: 25,
        emailsTotal: 0,
        emailsTotalPages: 1,
        emailsLoading: false,
        emailsError: null,
        emailFilterType: '',
        emailFilterStatus: '',
        emailFilterService: '',
        emailSearchTo: '',
        detailsPanel: {
            open: false,
            type: null, // 'user', 'event', 'execution', 'request', 'support', 'error'
            data: null,
        },
        emailTemplates: [],
        emailTemplatesLoading: false,
        emailTemplatesError: null,
        lifecycle: null,
        lifecycleLoading: false,
        lifecycleError: null,
        lifecycleRun: null,
        lifecycleRunning: false,
        lifecycleNotice: null,
        lifecycleTesting: null,
        selectedTemplate: null,
        templateSendForm: {
            visible: false,
            data: {},
            sending: false,
            error: null,
            success: false,
        },
    };

    // Listes paginées : clé du data-pagination rendu par renderPagination() -> état + chargeur.
    const paginatedLists = {
        users: { pageKey: 'usersPage', totalPagesKey: 'usersTotalPages', load: loadUsers },
        requests: { pageKey: 'requestsPage', totalPagesKey: 'requestsTotalPages', load: loadRequests },
        support: { pageKey: 'supportPage', totalPagesKey: 'supportTotalPages', load: loadSupportContacts },
        executions: { pageKey: 'executionsPage', totalPagesKey: 'executionsTotalPages', load: loadExecutions },
        events: { pageKey: 'eventsPage', totalPagesKey: 'eventsTotalPages', load: loadEvents },
        errors: { pageKey: 'errorsPage', totalPagesKey: 'errorsTotalPages', load: loadErrors },
        emails: { pageKey: 'emailsPage', totalPagesKey: 'emailsTotalPages', load: loadEmails },
    };

    function apiFetch(path, options = {}) {
        if (typeof authFetch === 'function') {
            return authFetch(path, options);
        }
        return baseApiFetch(path, options);
    }

    async function fetchJson(path, options = {}) {
        const response = await apiFetch(path, options);
        if (response.ok) {
            return response.json();
        }

        const data = await response.json().catch(() => ({}));
        const error = new Error(data?.error || `Erreur ${response.status}`);
        error.status = response.status;
        throw error;
    }

    async function loadData(showLoader = true) {
        try {
            if (showLoader) {
                state.loading = true;
                render();
            }
            state.error = null;
        } catch (error) {
            if (error.status === 403) {
                state.error = 'Accès administrateur requis.';
            } else if (error.status === 401) {
                state.error = 'Authentification requise.';
            } else {
                state.error = error.message || 'Erreur inattendue.';
            }
        } finally {
            state.loading = false;
            render();
        }
    }

    async function loadRequests({ page = 1, showSpinner = true } = {}) {
        try {
            state.requestsPage = page;
            if (showSpinner) {
                state.requestsLoading = true;
                if (state.currentTab === 'requests') {
                    render();
                }
            } else {
                state.requestsLoading = true;
            }

            const params = new URLSearchParams({
                page: String(page),
                limit: String(state.requestsPageSize),
            });
            if (state.filterType) {
                params.set('type', state.filterType);
            }
            if (state.filterStatus) {
                params.set('status', state.filterStatus);
            }

            const data = await fetchJson(`/api/admin/requests?${params.toString()}`);
            state.requests = Array.isArray(data?.requests) ? data.requests : [];
            state.requestsTotal = data?.total ?? 0;
            state.requestsTotalPages = Math.max(1, data?.totalPages ?? 1);
            state.requestsError = null;
            ensurePaginationBounds();
        } catch (error) {
            state.requestsError = error.message || 'Impossible de charger les demandes.';
        } finally {
            state.requestsLoading = false;
            if (state.currentTab === 'requests') {
                render();
            }
        }
    }

    async function loadUsers({ page = 1, showSpinner = true } = {}) {
        try {
            state.usersPage = page;
            if (showSpinner) {
                state.usersLoading = true;
                if (state.currentTab === 'users') {
                    render();
                }
            } else {
                state.usersLoading = true;
            }

            const params = new URLSearchParams({
                page: String(page),
                limit: String(state.usersPageSize),
            });
            if (state.usersSearch) {
                params.set('search', state.usersSearch);
            }

            const data = await fetchJson(`/api/admin/users?${params.toString()}`);
            state.users = Array.isArray(data?.users) ? data.users : [];
            state.usersTotal = data?.total ?? 0;
            state.usersTotalPages = Math.max(1, data?.totalPages ?? 1);
            state.usersError = null;
            ensurePaginationBounds();
        } catch (error) {
            state.usersError = error.message || 'Impossible de charger les utilisateurs.';
        } finally {
            state.usersLoading = false;
            if (state.currentTab === 'users') {
                render();
            }
        }
    }

    async function loadEmails({ page = 1, showSpinner = true } = {}) {
        try {
            state.emailsPage = page;
            if (showSpinner) {
                state.emailsLoading = true;
                if (state.currentTab === 'emails') {
                    render();
                }
            } else {
                state.emailsLoading = true;
            }

            const params = new URLSearchParams({
                page: String(page),
                limit: String(state.emailsPageSize),
            });
            if (state.emailFilterType) {
                params.set('emailType', state.emailFilterType);
            }
            if (state.emailFilterStatus) {
                params.set('status', state.emailFilterStatus);
            }
            if (state.emailFilterService) {
                params.set('service', state.emailFilterService);
            }
            if (state.emailSearchTo) {
                params.set('to', state.emailSearchTo);
            }

            const data = await fetchJson(`/api/admin/emails?${params.toString()}`);
            state.emails = Array.isArray(data?.emails) ? data.emails : [];
            state.emailsTotal = data?.total ?? 0;
            state.emailsTotalPages = Math.max(1, data?.totalPages ?? 1);
            state.emailsError = null;
            ensurePaginationBounds();
        } catch (error) {
            state.emailsError = error.message || 'Impossible de charger l\'historique des emails.';
        } finally {
            state.emailsLoading = false;
            if (state.currentTab === 'emails') {
                render();
            }
        }
    }

    async function loadLifecycle({ showSpinner = true } = {}) {
        try {
            if (showSpinner) {
                state.lifecycleLoading = true;
                if (state.currentTab === 'lifecycle') render();
            }
            state.lifecycle = await fetchJson('/api/admin/lifecycle');
            state.lifecycleError = null;
        } catch (error) {
            state.lifecycleError = error.message || 'Impossible de charger les parcours.';
        } finally {
            state.lifecycleLoading = false;
            if (state.currentTab === 'lifecycle') render();
        }
    }

    async function runLifecycle({ dryRun }) {
        state.lifecycleRunning = true;
        state.lifecycleNotice = null;
        render();
        try {
            state.lifecycleRun = await fetchJson('/api/admin/lifecycle/run', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ dryRun }),
            });
            if (!state.lifecycleRun.dryRun) {
                const total = Object.values(state.lifecycleRun.byJourney || {}).reduce((sum, n) => sum + n, 0);
                state.lifecycleNotice = { tone: 'success', text: `${total} message(s) envoyé(s).` };
                await loadLifecycle({ showSpinner: false });
            }
        } catch (error) {
            state.lifecycleNotice = { tone: 'danger', text: error.message || 'Échec de l’exécution.' };
        } finally {
            state.lifecycleRunning = false;
            render();
        }
    }

    async function testLifecycleJourney(journey) {
        state.lifecycleTesting = journey;
        state.lifecycleNotice = null;
        render();
        try {
            const result = await fetchJson('/api/admin/lifecycle/test', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ journey }),
            });
            const label = lifecycleJourneyLabel(journey);
            state.lifecycleNotice =
                result.status === 'sent'
                    ? { tone: 'success', text: `« ${label} » envoyé sur votre compte (${result.channels.join(' + ')}).` }
                    : {
                          tone: 'danger',
                          text: `« ${label} » non envoyé : ${(result.errors || []).join(' ; ') || 'aucun canal disponible (e-mail non configuré ou app mobile absente)'}.`,
                      };
            await loadLifecycle({ showSpinner: false });
        } catch (error) {
            state.lifecycleNotice = { tone: 'danger', text: error.message || 'Échec de l’envoi de test.' };
        } finally {
            state.lifecycleTesting = null;
            render();
        }
    }

    function lifecycleJourneyLabel(key) {
        return state.lifecycle?.journeys?.find((journey) => journey.key === key)?.label || key;
    }

    async function loadEmailTemplates({ showSpinner = true } = {}) {
        try {
            if (showSpinner) {
                state.emailTemplatesLoading = true;
                if (state.currentTab === 'templates') {
                    render();
                }
            }

            const data = await fetchJson('/api/admin/email-templates');
            state.emailTemplates = Array.isArray(data?.templates) ? data.templates : [];
            state.emailTemplatesError = null;
        } catch (error) {
            state.emailTemplatesError = error.message || 'Impossible de charger les templates d\'emails.';
        } finally {
            state.emailTemplatesLoading = false;
            if (state.currentTab === 'templates') {
                render();
            }
        }
    }

    async function loadSupportContacts({ page = 1, showSpinner = true } = {}) {
        try {
            state.supportPage = page;
            if (showSpinner) {
                state.supportLoading = true;
                if (state.currentTab === 'support') {
                    render();
                }
            } else {
                state.supportLoading = true;
            }

            const params = new URLSearchParams({
                page: String(page),
                limit: String(state.supportPageSize),
            });
            if (state.supportFilterStatus) {
                params.set('status', state.supportFilterStatus);
            }

            const data = await fetchJson(`/api/support/admin/all?${params.toString()}`);
            state.supportContacts = Array.isArray(data?.contacts) ? data.contacts : [];
            state.supportTotal = data?.total ?? 0;
            state.supportTotalPages = Math.max(1, data?.totalPages ?? 1);
            state.supportError = null;
            ensurePaginationBounds();
        } catch (error) {
            state.supportError = error.message || 'Impossible de charger les contacts support.';
        } finally {
            state.supportLoading = false;
            if (state.currentTab === 'support') {
                render();
            }
        }
    }

    async function loadOverview(showSpinner = true) {
        try {
            if (showSpinner) {
                state.overviewLoading = true;
                if (state.currentTab === 'overview') {
                    render();
                }
            }
            const data = await fetchJson('/api/admin/overview');
            state.overview = data || null;
            state.overviewError = null;
        } catch (error) {
            state.overviewError = error.message || 'Impossible de charger la vue globale.';
        } finally {
            state.overviewLoading = false;
            if (state.currentTab === 'overview') {
                render();
            }
        }
    }

    async function loadEvents({ page = 1, showSpinner = true } = {}) {
        try {
            state.eventsPage = page;
            if (showSpinner) {
                state.eventsLoading = true;
                if (state.currentTab === 'events') {
                    render();
                }
            } else {
                state.eventsLoading = true;
            }

            const params = new URLSearchParams({
                page: String(page),
                limit: String(state.eventsPageSize),
            });
            if (state.eventFilterType) {
                params.set('type', state.eventFilterType);
            }
            if (state.eventFilterUser) {
                params.set('userId', state.eventFilterUser);
            }

            const data = await fetchJson(`/api/admin/events?${params.toString()}`);
            state.events = Array.isArray(data?.events) ? data.events : [];
            state.eventsTotal = data?.total ?? 0;
            state.eventsTotalPages = Math.max(1, data?.totalPages ?? 1);
            state.eventsError = null;
            ensurePaginationBounds();
        } catch (error) {
            state.eventsError = error.message || 'Impossible de charger les événements.';
        } finally {
            state.eventsLoading = false;
            if (state.currentTab === 'events') {
                render();
            }
        }
    }

    async function searchEventsUsers(searchTerm) {
        try {
            eventsUsersLoading = true;
            if (state.currentTab === 'events') {
                render();
            }

            const params = new URLSearchParams({
                page: '1',
                limit: '10',
                search: searchTerm,
            });

            const data = await fetchJson(`/api/admin/users?${params.toString()}`);
            eventsUsersList = Array.isArray(data?.users) ? data.users : [];
        } catch (error) {
            log.error('[searchEventsUsers]', error);
            eventsUsersList = [];
        } finally {
            eventsUsersLoading = false;
            if (state.currentTab === 'events') {
                render();
            }
        }
    }

    async function loadExecutions({ page = 1, showSpinner = true } = {}) {
        try {
            state.executionsPage = page;
            if (showSpinner) {
                state.executionsLoading = true;
                if (state.currentTab === 'executions') {
                    render();
                }
            } else {
                state.executionsLoading = true;
            }

            const params = new URLSearchParams({
                page: String(page),
                limit: String(state.executionsPageSize),
            });
            if (state.executionFilterStatus) {
                params.set('status', state.executionFilterStatus);
            }

            const data = await fetchJson(`/api/admin/scenario-executions?${params.toString()}`);
            state.executions = Array.isArray(data?.executions) ? data.executions : [];
            state.executionsTotal = data?.total ?? 0;
            state.executionsTotalPages = Math.max(1, data?.totalPages ?? 1);
            state.executionsError = null;
            ensurePaginationBounds();
        } catch (error) {
            state.executionsError = error.message || 'Impossible de charger les exécutions.';
        } finally {
            state.executionsLoading = false;
            if (state.currentTab === 'executions') {
                render();
            }
        }
    }

    async function loadErrors({ page = 1, showSpinner = true } = {}) {
        try {
            state.errorsPage = page;
            if (showSpinner) {
                state.errorsLoading = true;
                if (state.currentTab === 'errors') {
                    render();
                }
            } else {
                state.errorsLoading = true;
            }

            const params = new URLSearchParams({
                page: String(page),
                pageSize: String(state.errorsPageSize),
            });
            if (state.errorFilterSource) {
                params.set('source', state.errorFilterSource);
            }
            if (state.errorFilterLevel) {
                params.set('level', state.errorFilterLevel);
            }
            if (state.errorFilterComponent) {
                params.set('component', state.errorFilterComponent);
            }
            if (state.errorFilterResolved !== '') {
                params.set('resolved', state.errorFilterResolved);
            }
            if (state.errorSearch) {
                params.set('search', state.errorSearch);
            }

            const data = await fetchJson(`/api/errors?${params.toString()}`);
            state.errors = Array.isArray(data?.errors) ? data.errors : [];
            state.errorsTotal = data?.pagination?.total ?? 0;
            state.errorsTotalPages = Math.max(1, data?.pagination?.totalPages ?? 1);
            state.errorsStats = data?.stats || null;
            state.errorsError = null;
            ensurePaginationBounds();
        } catch (error) {
            state.errorsError = error.message || 'Impossible de charger les erreurs.';
        } finally {
            state.errorsLoading = false;
            if (state.currentTab === 'errors') {
                render();
            }
        }
    }

    async function resolveError(errorId, resolutionNote = '') {
        try {
            const response = await apiFetch(`/api/errors/${errorId}/resolve`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ resolutionNote }),
            });

            if (!response.ok) {
                const data = await response.json().catch(() => ({}));
                throw new Error(data.error || 'Erreur lors de la résolution');
            }

            // Recharger les erreurs
            await loadErrors({ page: state.errorsPage, showSpinner: false });
        } catch (error) {
            log.error('Failed to resolve error', error);
            alert(`Erreur: ${error.message}`);
        }
    }

    function showDeleteRequestConfirmationModal(requestId, btn, onConfirm) {
        const deleteModal = createDeleteConfirmModal({
            title: 'Supprimer la demande',
            message: 'Êtes-vous sûr de vouloir supprimer cette demande ?',
            warningMessage: 'Cette action est <strong>irréversible</strong> et toutes les données associées seront définitivement perdues.',
            confirmText: 'Supprimer',
            cancelText: 'Annuler',
            onConfirm: onConfirm,
        });
        
        deleteModal.show();
    }

    function showDeleteSupportConfirmationModal(supportId, btn, onConfirm) {
        const deleteModal = createDeleteConfirmModal({
            title: 'Supprimer le ticket',
            message: 'Êtes-vous sûr de vouloir supprimer ce ticket support ?',
            warningMessage: 'Cette action est <strong>irréversible</strong> et toutes les données associées seront définitivement perdues.',
            confirmText: 'Supprimer',
            cancelText: 'Annuler',
            onConfirm: onConfirm,
        });
        
        deleteModal.show();
    }

    async function deleteRequest(requestId, btn) {
        if (!requestId) return;

        // Afficher la modale de confirmation
        showDeleteRequestConfirmationModal(requestId, btn, async () => {
            try {
                // Désactiver le bouton pendant la suppression
                btn.disabled = true;
                btn.style.opacity = '0.5';
                btn.style.cursor = 'not-allowed';

                const response = await apiFetch(`/api/admin/requests/${requestId}`, {
                    method: 'DELETE',
                });

                if (!response.ok) {
                    const data = await response.json().catch(() => ({}));
                    throw new Error(data.error || 'Erreur lors de la suppression');
                }

                // Recharger les demandes
                await loadRequests({ page: state.requestsPage, showSpinner: false });
            } catch (error) {
                console.error('Failed to delete request', error);
                alert(`Erreur: ${error.message}`);
                // Réactiver le bouton en cas d'erreur
                btn.disabled = false;
                btn.style.opacity = '1';
                btn.style.cursor = 'pointer';
            }
        });
    }

    async function closeSupportContact(supportId, btn) {
        if (!supportId) return;

        // Afficher la modale de confirmation
        showCloseSupportConfirmationModal(supportId, btn, async () => {
            try {
                // Désactiver le bouton pendant la clôture
                btn.disabled = true;
                btn.style.opacity = '0.5';
                btn.style.cursor = 'not-allowed';

                // Récupérer le contact pour préserver les adminNotes
                const contact = state.supportContacts.find(c => c.id === supportId);
                const adminNotes = contact?.adminNotes || '';

                const response = await apiFetch(`/api/support/admin/${supportId}/status`, {
                    method: 'PATCH',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ status: 'closed', adminNotes }),
                });

                if (!response.ok) {
                    const data = await response.json().catch(() => ({}));
                    throw new Error(data.error || 'Erreur lors de la clôture');
                }

                // Recharger les tickets support
                await loadSupportContacts({ page: state.supportPage, showSpinner: false });

                if (typeof window.showGlobalNotification === 'function') {
                    window.showGlobalNotification('success', 'Le ticket a été clôturé avec succès.');
                }
            } catch (error) {
                console.error('Failed to close support contact', error);
                if (typeof window.showGlobalNotification === 'function') {
                    window.showGlobalNotification('error', `Erreur: ${error.message}`);
                } else {
                    alert(`Erreur: ${error.message}`);
                }
                // Réactiver le bouton en cas d'erreur
                btn.disabled = false;
                btn.style.opacity = '1';
                btn.style.cursor = 'pointer';
            }
        });
    }

    function showCloseSupportConfirmationModal(supportId, btn, onConfirm) {
        const modal = document.createElement('div');
        modal.className = 'modal modal-validate-confirm';
        modal.style.cssText = 'display: flex; position: fixed; inset: 0; background-color: rgba(0, 0, 0, 0.7); backdrop-filter: blur(8px); align-items: center; justify-content: center; z-index: 3000;';

        modal.innerHTML = `
            <div class="modal-content modal-validate-content" style="max-width: 520px; width: 90%;">
                <button class="modal-close" aria-label="Fermer">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <line x1="18" y1="6" x2="6" y2="18"></line>
                        <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                </button>
                <div class="modal-validate-body">
                    <div class="modal-validate-mouse">
                        <img src="/images/souris/VEILLE.png" alt="Souris" class="mouse-animation" />
                    </div>
                    <div class="modal-validate-content-wrapper">
                        <h2 class="modal-validate-title">Clôturer le ticket</h2>
                        <p class="modal-validate-text">
                            Êtes-vous sûr de vouloir clôturer ce ticket ? Cette action est <strong>définitive</strong> et l'utilisateur ne pourra plus répondre à ce ticket.
                        </p>
                    </div>
                </div>
                <div class="modal-validate-footer">
                    <button class="modal-validate-btn modal-validate-btn-cancel" id="close-support-cancel">
                        <span>Annuler</span>
                    </button>
                    <button class="modal-validate-btn modal-validate-btn-confirm" id="close-support-confirm">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <polyline points="20 6 9 17 4 12"></polyline>
                        </svg>
                        <span>Clôturer</span>
                    </button>
                </div>
            </div>
        `;

        document.body.appendChild(modal);

        // Animation d'entrée
        setTimeout(() => {
            modal.classList.add('active');
        }, 10);

        // Fonction de fermeture
        const closeModal = () => {
            modal.classList.remove('active');
            setTimeout(() => {
                if (document.body.contains(modal)) {
                    modal.remove();
                }
            }, 300);
        };

        // Event listeners
        const closeBtn = modal.querySelector('.modal-close');
        const cancelBtn = modal.querySelector('#close-support-cancel');
        const confirmBtn = modal.querySelector('#close-support-confirm');

        closeBtn?.addEventListener('click', closeModal);
        cancelBtn?.addEventListener('click', closeModal);
        confirmBtn?.addEventListener('click', () => {
            closeModal();
            if (onConfirm) {
                onConfirm();
            }
        });

        modal.addEventListener('click', (e) => {
            if (e.target === modal) closeModal();
        });

        // Fermer avec Escape
        const escapeHandler = (e) => {
            if (e.key === 'Escape') {
                closeModal();
                document.removeEventListener('keydown', escapeHandler);
            }
        };
        document.addEventListener('keydown', escapeHandler);
    }

    async function deleteSupportContact(supportId, btn) {
        if (!supportId) return;

        // Afficher la modale de confirmation
        showDeleteSupportConfirmationModal(supportId, btn, async () => {
            try {
                // Désactiver le bouton pendant la suppression
                btn.disabled = true;
                btn.style.opacity = '0.5';
                btn.style.cursor = 'not-allowed';

                const response = await apiFetch(`/api/support/admin/${supportId}`, {
                    method: 'DELETE',
                });

                if (!response.ok) {
                    const data = await response.json().catch(() => ({}));
                    throw new Error(data.error || 'Erreur lors de la suppression');
                }

                // Recharger les tickets support
                await loadSupportContacts({ page: state.supportPage, showSpinner: false });
            } catch (error) {
                console.error('Failed to delete support contact', error);
                if (typeof window.showGlobalNotification === 'function') {
                    window.showGlobalNotification('error', `Erreur: ${error.message}`);
                } else {
                    alert(`Erreur: ${error.message}`);
                }
                // Réactiver le bouton en cas d'erreur
                btn.disabled = false;
                btn.style.opacity = '1';
                btn.style.cursor = 'pointer';
            }
        });
    }

    function showCreateUserModal() {
        const modal = document.createElement('div');
        modal.className = 'modal modal-create-user ad-modal';

        // Utiliser l'environnement global
        const globalEnv = getCurrentEnvironment();
        
        // Obtenir le label de l'environnement
        const getEnvLabel = () => {
            if (globalEnv === 'auto') return 'Auto-détecté';
            return API_ENVIRONMENTS[globalEnv.toUpperCase()]?.label || globalEnv;
        };
        
        modal.innerHTML = `
            <div class="modal-content ad-modal-card">
                <div class="ad-modal-head">
                    <span class="ad-modal-icon material-symbols-outlined" aria-hidden="true">person_add</span>
                    <div class="ad-modal-heading">
                        <h2 class="ad-modal-title">Créer un utilisateur</h2>
                        <p class="ad-modal-sub">Environnement : <strong>${escapeHtml(getEnvLabel())}</strong> · modifiable depuis le menu.</p>
                    </div>
                    <button class="modal-close rd-icon-btn" aria-label="Fermer" type="button">
                        <span class="material-symbols-outlined">close</span>
                    </button>
                </div>

                <form id="create-user-form" class="ad-form ad-modal-body">
                    <div class="ad-field">
                        <label class="ad-label" for="create-user-email">Email <span class="ad-required">*</span></label>
                        <input id="create-user-email" class="ad-input" type="email" name="email" required placeholder="user@example.com" />
                    </div>
                    <div class="ad-field">
                        <label class="ad-label" for="create-user-password">Mot de passe <span class="ad-required">*</span></label>
                        <input id="create-user-password" class="ad-input" type="password" name="password" required minlength="6" placeholder="Minimum 6 caractères" />
                    </div>
                    <div class="ad-field-row">
                        <div class="ad-field">
                            <label class="ad-label" for="create-user-name">Nom complet</label>
                            <input id="create-user-name" class="ad-input" type="text" name="name" placeholder="John Doe" />
                        </div>
                        <div class="ad-field">
                            <label class="ad-label" for="create-user-role">Rôle</label>
                            <select id="create-user-role" class="ad-input" name="role">
                                <option value="user">Utilisateur</option>
                                <option value="admin">Administrateur</option>
                            </select>
                        </div>
                    </div>
                    <div class="ad-field-row">
                        <div class="ad-field">
                            <label class="ad-label" for="create-user-title">Titre</label>
                            <input id="create-user-title" class="ad-input" type="text" name="title" placeholder="Développeur, Manager…" />
                        </div>
                        <div class="ad-field">
                            <label class="ad-label" for="create-user-department">Département</label>
                            <input id="create-user-department" class="ad-input" type="text" name="department" placeholder="IT, RH, Marketing…" />
                        </div>
                    </div>

                    <div id="create-user-error" class="ad-alert ad-alert--danger" style="display: none;"></div>

                    <div class="ad-modal-foot">
                        <button type="button" class="modal-cancel rd-btn rd-btn-light">Annuler</button>
                        <button type="submit" class="modal-submit rd-btn rd-btn-night">Créer l'utilisateur</button>
                    </div>
                </form>
            </div>
        `;

        document.body.appendChild(modal);

        // Animation d'entrée
        setTimeout(() => {
            modal.classList.add('active');
        }, 10);

        const form = modal.querySelector('#create-user-form');
        const submitBtn = modal.querySelector('.modal-submit');
        const errorDiv = modal.querySelector('#create-user-error');

        const closeModal = () => {
            modal.classList.remove('active');
            setTimeout(() => {
                document.body.removeChild(modal);
            }, 300);
        };

        modal.querySelector('.modal-close').addEventListener('click', closeModal);
        modal.querySelector('.modal-cancel').addEventListener('click', closeModal);
        modal.addEventListener('click', (e) => {
            if (e.target === modal) closeModal();
        });

        form.addEventListener('submit', async (e) => {
            e.preventDefault();
            
            const formData = new FormData(form);
            
            const data = {
                email: formData.get('email'),
                password: formData.get('password'),
                name: formData.get('name') || undefined,
                role: formData.get('role'),
                title: formData.get('title') || undefined,
                department: formData.get('department') || undefined,
            };

            // Validation côté client
            if (!data.email || !data.password) {
                errorDiv.textContent = 'Email et mot de passe requis.';
                errorDiv.style.display = 'block';
                return;
            }

            if (data.password.length < 6) {
                errorDiv.textContent = 'Le mot de passe doit contenir au moins 6 caractères.';
                errorDiv.style.display = 'block';
                return;
            }

            // Désactiver le bouton pendant la création
            submitBtn.disabled = true;
            submitBtn.textContent = 'Création en cours...';
            errorDiv.style.display = 'none';

            try {
                // apiFetch délègue à authFetch (environnement global) et retombe sur le client de base sinon
                const response = await apiFetch('/api/admin/users', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(data),
                });

                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({}));
                    throw new Error(errorData.error || 'Erreur lors de la création de l\'utilisateur');
                }

                const result = await response.json();

                // Afficher une notification de succès
                if (typeof window.showGlobalNotification === 'function') {
                    window.showGlobalNotification('success', `Utilisateur créé avec succès : ${result.user.email}. Un email de vérification a été envoyé.`);
                } else {
                    alert(`Utilisateur créé avec succès : ${result.user.email}. Un email de vérification a été envoyé.`);
                }

                // Recharger la liste des utilisateurs
                await loadUsers({ page: state.usersPage, search: state.usersSearch, showSpinner: false });

                // Fermer le modal
                closeModal();
            } catch (error) {
                console.error('Failed to create user', error);
                errorDiv.textContent = error.message;
                errorDiv.style.display = 'block';
                submitBtn.disabled = false;
                submitBtn.textContent = 'Créer l\'utilisateur';
            }
        });
    }

    function render() {
        const content = container.querySelector('.admin-content');
        if (!content) return;

        if (state.loading) {
            content.innerHTML = '<p class="admin-empty">Chargement des données...</p>';
            return;
        }

        if (state.error) {
            const retryLabel =
                state.error && state.error.includes('Authentification')
                    ? 'Se reconnecter'
                    : 'Réessayer';
            content.innerHTML = `
                <div class="admin-error">
                    <p>${state.error}</p>
                    <button class="btn-primary admin-retry">${retryLabel}</button>
                </div>
            `;
            content.querySelector('.admin-retry').addEventListener('click', () => loadData(true));
            return;
        }

        ensurePaginationBounds();

        const focusToRestore = captureFocus(content);

        content.innerHTML = `
            <div class="admin-content">
                <div class="admin-content-wrapper">
                    ${renderPageHeader(state.currentTab, renderPageActions())}
                    <div class="admin-tab-panel">
                        ${renderActiveTab()}
                    </div>
                </div>
            </div>
        `;

        restoreFocus(content, focusToRestore);

        // Update sidebar active state
        const sidebar = container.querySelector('.admin-sidebar-nav');
        if (sidebar) {
            const navItems = sidebar.querySelectorAll('.admin-nav-item');
            navItems.forEach(item => {
                if (item.dataset.tab === state.currentTab) {
                    item.classList.add('active');
                } else {
                    item.classList.remove('active');
                }
            });

            // Update errors badge
            const errorsItem = sidebar.querySelector('[data-tab="errors"]');
            if (errorsItem) {
                const badge = errorsItem.querySelector('.admin-nav-badge');
                if (badge) {
                    if (state.errorsStats?.unresolved > 0) {
                        badge.textContent = state.errorsStats.unresolved;
                        badge.style.display = 'flex';
                    } else {
                        badge.style.display = 'none';
                    }
                }
            }
        }

        bindTabInteractions(content);
    }

    /**
     * Le contenu est entièrement ré-rendu (innerHTML) : sans ça, le champ de recherche
     * perdrait le focus dès que les résultats arrivent.
     */
    function captureFocus(content) {
        const active = document.activeElement;
        if (active && active.id && content.contains(active) && active.matches('input, textarea')) {
            return {
                id: active.id,
                start: typeof active.selectionStart === 'number' ? active.selectionStart : null,
                end: typeof active.selectionEnd === 'number' ? active.selectionEnd : null,
            };
        }
        // Le champ a pu disparaître lors d'un render précédent (état "Chargement...") :
        // on le restaure s'il revient et que l'utilisateur n'a rien focalisé d'autre entre-temps.
        if (pendingFocus && (!active || active === document.body)) {
            return pendingFocus;
        }
        pendingFocus = null;
        return null;
    }

    function restoreFocus(content, focus) {
        if (!focus) return;
        const field = content.querySelector(`[id="${focus.id.replace(/["\\]/g, '\\$&')}"]`);
        if (!field) {
            pendingFocus = focus;
            return;
        }
        pendingFocus = null;
        field.focus();
        if (focus.start !== null && typeof field.setSelectionRange === 'function') {
            const length = field.value.length;
            try {
                field.setSelectionRange(Math.min(focus.start, length), Math.min(focus.end ?? focus.start, length));
            } catch {
                // type="email" etc. ne supportent pas setSelectionRange
            }
        }
    }

    function ensurePaginationBounds() {
        const usersPages = Math.max(
            1,
            state.usersTotalPages || Math.ceil(state.usersTotal / state.usersPageSize)
        );
        if (state.usersPage > usersPages) {
            state.usersPage = usersPages;
        }
        if (state.usersPage < 1) {
            state.usersPage = 1;
        }

        const requestsPages = Math.max(
            1,
            state.requestsTotalPages || Math.ceil(state.requestsTotal / state.requestsPageSize)
        );
        if (state.requestsPage > requestsPages) {
            state.requestsPage = requestsPages;
        }
        if (state.requestsPage < 1) {
            state.requestsPage = 1;
        }

        const eventsPages = Math.max(
            1,
            state.eventsTotalPages || Math.ceil(state.eventsTotal / state.eventsPageSize)
        );
        if (state.eventsPage > eventsPages) {
            state.eventsPage = eventsPages;
        }
        if (state.eventsPage < 1) {
            state.eventsPage = 1;
        }

        const executionsPages = Math.max(
            1,
            state.executionsTotalPages ||
            Math.ceil(state.executionsTotal / state.executionsPageSize)
        );
        if (state.executionsPage > executionsPages) {
            state.executionsPage = executionsPages;
        }
        if (state.executionsPage < 1) {
            state.executionsPage = 1;
        }

        const supportPages = Math.max(
            1,
            state.supportTotalPages || Math.ceil(state.supportTotal / state.supportPageSize)
        );
        if (state.supportPage > supportPages) {
            state.supportPage = supportPages;
        }
        if (state.supportPage < 1) {
            state.supportPage = 1;
        }
    }

    function renderPageActions() {
        if (state.currentTab === 'users') {
            return `
                <button class="rd-btn rd-btn-night admin-create-user" type="button">
                    <span class="material-symbols-outlined">person_add</span>
                    Créer un utilisateur
                </button>
            `;
        }
        if (state.currentTab === 'lifecycle') {
            const busy = state.lifecycleRunning ? 'disabled' : '';
            return `
                <button class="rd-btn rd-btn-light" data-lifecycle-run="dry" type="button" ${busy}>
                    <span class="material-symbols-outlined">science</span>
                    ${state.lifecycleRunning ? 'Calcul…' : 'Simuler'}
                </button>
                ${state.lifecycle?.sendingEnabled
                    ? `<button class="rd-btn rd-btn-night" data-lifecycle-run="send" type="button" ${busy}>
                        <span class="material-symbols-outlined">send</span>
                        Exécuter maintenant
                    </button>`
                    : ''}
            `;
        }
        if (state.currentTab === 'templates') {
            return `
                <button class="rd-btn rd-btn-night" id="show-custom-email-form" type="button">
                    <span class="material-symbols-outlined">edit_square</span>
                    Email personnalisé
                </button>
            `;
        }
        return '';
    }

    function renderActiveTab() {
        switch (state.currentTab) {
            case 'overview':
                return renderOverviewTab();
            case 'requests':
                return renderRequestsTab();
            case 'support':
                return renderSupportTab();
            case 'events':
                return renderEventsTab();
            case 'executions':
                return renderExecutionsTab();
            case 'errors':
                return renderErrorsTab();
            case 'emails':
                return renderEmailsTab();
            case 'templates':
                return renderTemplatesTab();
            case 'lifecycle':
                return renderLifecycleTab();
            case 'users':
            default:
                return renderUsersTab();
        }
    }

    function renderOverviewTab() {
        if (state.overviewLoading) {
            return `
                <section class="admin-section">
                    <p class="admin-empty">Chargement des indicateurs...</p>
                </section>
            `;
        }

        if (state.overviewError) {
            return `
                <section class="admin-section">
                    <div class="admin-error">
                        <p>${escapeHtml(state.overviewError)}</p>
                    </div>
                </section>
            `;
        }

        const metrics = state.overview?.metrics || {};
        const charts = state.overview?.charts || {};

        const cards = [
            {
                icon: 'group',
                label: 'Utilisateurs',
                value: metrics.totalUsers ?? 0,
                hint: `${metrics.newUsersLast7 ?? 0} nouveaux sur 7 jours`,
            },
            {
                icon: 'account_tree',
                label: 'Scénarios',
                value: metrics.totalScenarios ?? 0,
                hint: `${metrics.eventsLast30 ?? 0} événements sur 30 jours`,
            },
            {
                icon: 'person_check',
                label: 'Utilisateurs actifs (30 jours)',
                value: metrics.activeUsersLast30 ?? 0,
                hint: 'Distincts',
            },
            {
                icon: 'error',
                tone: 'danger',
                label: "Erreurs d'exécution (30 jours)",
                value: metrics.executionErrorsLast30 ?? 0,
                hint: 'Scénarios en échec',
            },
            {
                icon: 'check_circle',
                tone: 'success',
                label: 'Exécutions réussies (30 jours)',
                value: metrics.executionSuccessLast30 ?? 0,
                hint: `${metrics.totalExecutionsLast30 ?? 0} au total`,
            },
            {
                icon: 'percent',
                label: 'Taux de succès',
                value: `${metrics.successRate ?? 0}%`,
                hint: 'Sur 30 jours',
            },
            {
                icon: 'inbox',
                tone: 'warning',
                label: 'Demandes en attente',
                value: metrics.pendingRequests ?? 0,
                hint: 'À traiter',
            },
            {
                icon: 'share',
                label: 'Scénarios partagés',
                value: metrics.totalShares ?? 0,
                hint: 'Partages actifs',
            },
            {
                icon: 'apps',
                label: 'Applications',
                value: metrics.totalApplications ?? 0,
                hint: 'Disponibles',
            },
        ];

        // Cartes avec graphiques
        const chartCards = [
            {
                label: 'Exécutions (30 jours)',
                value: metrics.totalExecutionsLast30 ?? 0,
                hint: `${metrics.executionSuccessLast30 ?? 0} réussies, ${metrics.executionErrorsLast30 ?? 0} erreurs`,
                chart: renderExecutionsChart(charts.executions || []),
            },
            {
                label: 'Événements (30 jours)',
                value: metrics.eventsLast30 ?? 0,
                hint: 'Activité quotidienne',
                chart: renderEventsChart(charts.events || []),
            },
            {
                label: 'Nouveaux utilisateurs (30 jours)',
                value: metrics.newUsersLast7 ?? 0,
                hint: 'Inscriptions quotidiennes',
                chart: renderUsersChart(charts.newUsers || []),
            },
        ];

        return `
            <div class="ad-kpis">
                ${cards
                .map(
                    (card) => `
                    <div class="ad-kpi${card.tone ? ` ad-kpi--${card.tone}` : ''}">
                        <div class="ad-kpi-head">
                            <span class="ad-kpi-icon material-symbols-outlined" aria-hidden="true">${card.icon}</span>
                            <span class="ad-kpi-label">${escapeHtml(card.label)}</span>
                        </div>
                        <strong class="ad-kpi-value">${escapeHtml(formatMetricValue(card.value))}</strong>
                        <span class="ad-kpi-hint">${escapeHtml(card.hint || '')}</span>
                    </div>
                `
                )
                .join('')}
            </div>
            <div class="ad-charts">
                ${chartCards
                .map(
                    (card) => `
                    <div class="ad-chart-card">
                        <div class="ad-chart-head">
                            <h3 class="ad-chart-title">${escapeHtml(card.label)}</h3>
                            <span class="ad-chart-hint">${escapeHtml(card.hint || '')}</span>
                        </div>
                        <strong class="ad-chart-value">${escapeHtml(formatMetricValue(card.value))}</strong>
                        <div class="admin-chart-container">
                            ${card.chart}
                        </div>
                    </div>
                `
                )
                .join('')}
            </div>
        `;
    }

    function renderEventsTab() {
        if (state.eventsLoading && state.events.length === 0) {
            return `
                <section class="admin-section">
                    <p class="admin-empty">Chargement des événements...</p>
                </section>
            `;
        }

        if (state.eventsError) {
            return `
                <section class="admin-section">
                    <div class="admin-error">
                        <p>${escapeHtml(state.eventsError)}</p>
                        <button class="btn-secondary admin-refresh" data-target="events">Réessayer</button>
                    </div>
                </section>
            `;
        }

        const events = state.events || [];
        const tableMarkup =
            events.length === 0
                ? renderAdminEmptyState('events')
                : `
                    <div class="admin-table-wrapper-modern">
                        <table class="admin-table-modern">
                            <thead>
                                <tr>
                                    <th>Type</th>
                                    <th>Description</th>
                                    <th>Utilisateur</th>
                                    <th>Cible</th>
                                    <th>Date</th>
                                </tr>
                            </thead>
                            <tbody>
                                ${events.map((event) => renderEventRow(event)).join('')}
                            </tbody>
                        </table>
                    </div>
            `;

        const eventTypes = [
            { key: '', label: 'Tous les types' },
            { key: 'scenario.created', label: 'Scénario créé' },
            { key: 'scenario.updated', label: 'Scénario modifié' },
            { key: 'scenario.deleted', label: 'Scénario supprimé' },
            { key: 'scenario.application_added', label: 'Application ajoutée' },
            { key: 'scenario.application_removed', label: 'Application retirée' },
            { key: 'scenario.execution_success', label: 'Exécution réussie' },
            { key: 'scenario.execution_warning', label: 'Exécution avec avertissement' },
            { key: 'scenario.execution_error', label: 'Exécution en erreur' },
            { key: 'request.created', label: 'Demande créée' },
            { key: 'request.message_added', label: 'Message ajouté' },
            { key: 'request.message_deleted', label: 'Message supprimé' },
            { key: 'request.status_updated', label: 'Statut mis à jour' },
        ];

        // Afficher la liste uniquement si on est en train de rechercher (plus de 2 caractères) et qu'on n'a pas encore sélectionné un utilisateur
        const isSearching =
            state.eventFilterUserSearch &&
            state.eventFilterUserSearch.length >= 2 &&
            !state.eventFilterUser;
        const usersListHtml =
            isSearching && eventsUsersList.length > 0
                ? `<ul class="admin-users-autocomplete-list">
                ${eventsUsersList
                    .map(
                        (user) => `
                    <li class="admin-users-autocomplete-item" data-user-id="${escapeHtml(user._id)}" data-user-username="${escapeHtml(user.username || '')}">
                        <span class="admin-users-autocomplete-username">${escapeHtml(user.username || user._id)}</span>
                        ${user.email ? `<span class="admin-users-autocomplete-email">${escapeHtml(user.email)}</span>` : ''}
                    </li>
                `
                    )
                    .join('')}
            </ul>`
                : isSearching && !eventsUsersLoading && eventsUsersList.length === 0
                    ? '<p class="admin-users-autocomplete-empty">Aucun utilisateur trouvé</p>'
                    : '';

        return `
            <section class="admin-section">
                <div class="ad-toolbar">
                    <div class="ad-filter">
                        <span class="ad-filter-label">Type d'événement</span>
                        <div class="admin-filter-pills">
                            ${eventTypes
                .map(
                    (type) => `
                                <button 
                                    class="admin-filter-pill ${state.eventFilterType === type.key ? 'active' : ''}" 
                                    data-filter="events-type"
                                    data-value="${type.key}"
                                    type="button"
                                >
                                    ${escapeHtml(type.label)}
                                </button>
                            `
                )
                .join('')}
                        </div>
                    </div>
                    <div class="admin-filter-group admin-filter-group-user">
                        <label for="admin-event-user">ID utilisateur</label>
                        <div class="cmd-center-search-wrapper admin-users-search-wrapper">
                            <svg class="cmd-center-search-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                <circle cx="11" cy="11" r="8"></circle>
                                <path d="m21 21-4.35-4.35"></path>
                            </svg>
                            <input 
                                id="admin-event-user" 
                                name="userId" 
                                type="text" 
                                class="cmd-center-search-input"
                                placeholder="Rechercher un utilisateur..." 
                                value="${escapeHtml(state.eventFilterUserSearch || state.eventFilterUser || '')}" 
                                autocomplete="off"
                            />
                            ${usersListHtml}
                        </div>
                    </div>
                </div>
                ${tableMarkup}
                ${renderPagination('events', state.eventsPage, state.eventsTotalPages, state.eventsTotal, state.eventsPageSize)}
            </section>
        `;
    }

    function renderExecutionsTab() {
        if (state.executionsLoading && state.executions.length === 0) {
            return `
                <section class="admin-section">
                    <p class="admin-empty">Chargement des exécutions...</p>
                </section>
            `;
        }

        if (state.executionsError) {
            return `
                <section class="admin-section">
                    <div class="admin-error">
                        <p>${escapeHtml(state.executionsError)}</p>
                        <button class="btn-secondary admin-refresh" data-target="executions">Réessayer</button>
                    </div>
                </section>
            `;
        }

        const executions = state.executions || [];
        const statusLabel = {
            success: 'Succès',
            warning: 'Avertissement',
            error: 'Erreur',
        };

        const executionsMarkup =
            executions.length === 0
                ? renderAdminEmptyState('executions')
                : `
                    <div class="admin-table-wrapper-modern">
                        <table class="admin-table-modern">
                            <thead>
                                <tr>
                                    <th>Scénario</th>
                                    <th>Statut</th>
                                    <th>Utilisateur</th>
                                    <th>Durée</th>
                                    <th>Date</th>
                                </tr>
                            </thead>
                            <tbody>
                                ${executions
                    .map(
                        (execution) => {
                            return `
                                    <tr data-execution-id="${execution.id}" data-type="execution" class="admin-table-row-clickable">
                                        <td class="ad-cell-strong">${escapeHtml(execution.scenarioName || '—')}</td>
                                        <td>${pill(statusLabel[execution.status] || execution.status, toneFor('execution', execution.status))}</td>
                                        <td class="ad-cell-muted">${escapeHtml(execution.userEmail || '—')}</td>
                                        <td class="ad-cell-muted ad-cell-num">${formatDurationLabel(execution.durationMs)}</td>
                                        <td class="ad-cell-muted ad-cell-date">${formatDateTime(execution.createdAt)}</td>
                                    </tr>
                                `;
                        }
                    )
                    .join('')}
                            </tbody>
                        </table>
                    </div>
            `;

        const statusOptions = [
            { key: '', label: 'Tous les statuts' },
            { key: 'error', label: 'Erreurs' },
            { key: 'warning', label: 'Avertissements' },
            { key: 'success', label: 'Succès' },
        ];

        return `
            <section class="admin-section">
                <div class="ad-toolbar">
                    <div class="admin-filter-pills">
                        ${statusOptions
                .map(
                    (option) => `
                            <button 
                                class="admin-filter-pill ${state.executionFilterStatus === option.key ? 'active' : ''}" 
                                data-filter="executions-status"
                                data-value="${option.key}"
                                type="button"
                            >
                                ${escapeHtml(option.label)}
                            </button>
                        `
                )
                .join('')}
                    </div>
                </div>
                ${executionsMarkup}
                ${renderPagination('executions', state.executionsPage, state.executionsTotalPages, state.executionsTotal, state.executionsPageSize)}
            </section>
        `;
    }

    function renderUsersTab() {
        if (state.usersLoading && state.users.length === 0) {
            return `
                <section class="admin-section">
                    <p class="admin-empty">Chargement des utilisateurs...</p>
                </section>
            `;
        }

        if (state.usersError) {
            return `
                <section class="admin-section">
                    <div class="admin-error">
                        <p>${escapeHtml(state.usersError)}</p>
                    </div>
                </section>
            `;
        }

        const users = state.users || [];
        const tableMarkup =
            users.length === 0
                ? renderAdminEmptyState('users')
                : `
                    <div class="admin-table-wrapper-modern">
                        <table class="admin-table-modern">
                            <thead>
                                <tr>
                                    <th>Email</th>
                                    <th>Identifiant</th>
                                    <th>Nom</th>
                                    <th>Rôle</th>
                                    <th>Dernière connexion</th>
                                    <th>Créé le</th>
                                </tr>
                            </thead>
                            <tbody>
                                ${users
                    .map(
                        (user) => {
                            const emailVerifiedBadge = user.emailVerified 
                                ? '<span class="material-symbols-outlined ad-verified" title="Email vérifié">verified</span>'
                                : '<span class="material-symbols-outlined ad-unverified" title="Email non vérifié">pending</span>';

                            return `
                                    <tr data-user-id="${user._id || user.id}" class="admin-table-row-clickable" data-type="user">
                                        <td>
                                            <div class="ad-user-cell">
                                                <span class="ad-avatar" aria-hidden="true">${escapeHtml((user.name || user.email || '?').trim().charAt(0).toUpperCase())}</span>
                                                <span class="ad-cell-strong">${escapeHtml(user.email || '')}</span>
                                                ${emailVerifiedBadge}
                                            </div>
                                        </td>
                                        <td>${user.username ? `<code class="admin-user-id">${escapeHtml(user.username)}</code>` : '<span class="ad-muted">—</span>'}</td>
                                        <td>${escapeHtml(user.name || '—')}</td>
                                        <td>${pill(user.role === 'admin' ? 'Admin' : 'Utilisateur', user.role === 'admin' ? 'accent' : 'neutral', { dot: false })}</td>
                                        <td class="ad-cell-muted ad-cell-date">${formatLastLogin(user.lastLoginAt)}</td>
                                        <td class="ad-cell-muted ad-cell-date">${formatDate(user.createdAt)}</td>
                                    </tr>
                                `;
                        }
                    )
                    .join('')}
                            </tbody>
                        </table>
                    </div>
            `;

        return `
            <section class="admin-section">
                <div class="ad-toolbar">
                    <div class="admin-filter-group ad-filter-grow">
                        <label for="admin-users-search">Rechercher</label>
                        <div class="cmd-center-search-wrapper">
                            <svg class="cmd-center-search-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                <circle cx="11" cy="11" r="8"></circle>
                                <path d="m21 21-4.35-4.35"></path>
                            </svg>
                            <input 
                                id="admin-users-search" 
                                name="search" 
                                type="text" 
                                class="cmd-center-search-input"
                                placeholder="Email ou identifiant" 
                                value="${escapeHtml(state.usersSearch)}"
                            />
                        </div>
                    </div>
                </div>
                ${tableMarkup}
                ${renderPagination('users', state.usersPage, state.usersTotalPages, state.usersTotal, state.usersPageSize)}
            </section>
        `;
    }

    function renderRequestsTab() {
        if (state.requestsLoading && state.requests.length === 0) {
            return `
                <section class="admin-section">
                    <p class="admin-empty">Chargement des demandes...</p>
                </section>
            `;
        }

        if (state.requestsError) {
            return `
                <section class="admin-section">
                    <div class="admin-error">
                        <p>${escapeHtml(state.requestsError)}</p>
                    </div>
                </section>
            `;
        }

        const requests = state.requests || [];

        const typeFilters = [
            { key: '', label: 'Toutes' },
            { key: 'application', label: 'Applications' },
            { key: 'command', label: 'Commandes' },
            { key: 'roadmap', label: 'Suggestion' },
        ];

        const statusFilters = [
            { key: '', label: 'Tous statuts' },
            ...REQUEST_STATUSES.map(s => ({ key: s.value, label: s.label })),
        ];

        return `
            <section class="admin-section">
                <div class="ad-toolbar">
                    <div class="ad-filter">
                        <span class="ad-filter-label">Type</span>
                        <div class="admin-filter-pills">
                            ${typeFilters.map(filter => `
                                <button 
                                    class="admin-filter-pill ${state.filterType === filter.key ? 'active' : ''}" 
                                    data-filter="requests-type"
                                    data-value="${filter.key}"
                                    type="button"
                                >
                                    ${escapeHtml(filter.label)}
                                </button>
                            `).join('')}
                        </div>
                    </div>
                    <div class="ad-filter">
                        <span class="ad-filter-label">Statut</span>
                        <div class="admin-filter-pills">
                            ${statusFilters.map(filter => `
                                <button 
                                    class="admin-filter-pill ${state.filterStatus === filter.key ? 'active' : ''}" 
                                    data-filter="requests-status"
                                    data-value="${filter.key}"
                                    type="button"
                                >
                                    ${escapeHtml(filter.label)}
                                </button>
                            `).join('')}
                        </div>
                    </div>
                </div>
                ${requests.length === 0
                ? renderAdminEmptyState('requests')
                : `
                    <div class="admin-table-wrapper-modern">
                        <table class="admin-table-modern">
                            <thead>
                                <tr>
                                    <th>Type</th>
                                    <th>Titre</th>
                                    <th>Statut</th>
                                    <th>Utilisateur</th>
                                    <th>Date</th>
                                    <th>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                ${requests.map((request) => renderRequestRow(request)).join('')}
                            </tbody>
                        </table>
                    </div>
                `
            }
                ${renderPagination('requests', state.requestsPage, state.requestsTotalPages, state.requestsTotal, state.requestsPageSize)}
            </section>
        `;
    }

    function renderEmailsTab() {
        if (state.emailsLoading && state.emails.length === 0) {
            return `
                <section class="admin-section">
                    <p class="admin-empty">Chargement de l'historique des emails...</p>
                </section>
            `;
        }

        if (state.emailsError) {
            return `
                <section class="admin-section">
                    <div class="admin-error">
                        <p>${escapeHtml(state.emailsError)}</p>
                    </div>
                </section>
            `;
        }

        const emails = state.emails || [];

        const emailTypeFilters = [
            { key: '', label: 'Tous les types' },
            { key: 'verification', label: 'Vérification' },
            { key: 'password_reset', label: 'Réinitialisation mot de passe' },
            { key: 'scenario_shared', label: 'Scénario partagé' },
            { key: 'scenario_updated', label: 'Scénario mis à jour' },
            { key: 'application_request', label: 'Demande d\'application' },
            { key: 'admin_custom', label: 'Email admin' },
        ];

        const statusFilters = [
            { key: '', label: 'Tous les statuts' },
            { key: 'success', label: 'Succès' },
            { key: 'failed', label: 'Échec' },
        ];

        const serviceFilters = [
            { key: '', label: 'Tous les services' },
            { key: 'sendgrid', label: 'SendGrid' },
            { key: 'nodemailer', label: 'Nodemailer' },
        ];

        const emailTypeLabels = {
            verification: 'Vérification',
            password_reset: 'Réinitialisation',
            scenario_shared: 'Scénario partagé',
            scenario_updated: 'Scénario mis à jour',
            application_request: 'Demande d\'app',
            admin_custom: 'Email admin',
            support_status_change: 'Statut support',
            support_reply: 'Réponse support',
        };

        function renderEmailRow(email) {
            const date = new Date(email.createdAt);
            const formattedDate = date.toLocaleString('fr-FR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
            });

            return `
                <tr class="admin-table-row-clickable" data-email-id="${email.id}" data-type="email">
                    <td><span class="ad-type">${escapeHtml(emailTypeLabels[email.emailType] || email.emailType)}</span></td>
                    <td><div class="ad-ellipsis ad-cell-strong" title="${escapeHtml(email.subject)}">${escapeHtml(email.subject)}</div></td>
                    <td class="ad-cell-muted"><div class="ad-ellipsis ad-ellipsis--sm" title="${escapeHtml(email.to)}">${escapeHtml(email.to)}</div></td>
                    <td>${pill(email.status === 'success' ? 'Envoyé' : 'Échec', toneFor('email', email.status))}</td>
                    <td class="ad-cell-muted">${escapeHtml(email.service)}</td>
                    <td class="ad-cell-muted ad-cell-date">${escapeHtml(formattedDate)}</td>
                </tr>
            `;
        }

        return `
            <section class="admin-section">
                <div class="ad-toolbar ad-toolbar--stack">
                    <div class="ad-filter-row">
                        <div class="ad-filter">
                            <span class="ad-filter-label">Type</span>
                            <div class="admin-filter-pills">
                                ${emailTypeFilters.map(filter => `
                                    <button 
                                        class="admin-filter-pill ${state.emailFilterType === filter.key ? 'active' : ''}" 
                                        data-filter="emails-type"
                                        data-value="${filter.key}"
                                        type="button"
                                    >
                                        ${escapeHtml(filter.label)}
                                    </button>
                                `).join('')}
                            </div>
                        </div>
                        <div class="ad-filter">
                            <span class="ad-filter-label">Statut</span>
                            <div class="admin-filter-pills">
                                ${statusFilters.map(filter => `
                                    <button 
                                        class="admin-filter-pill ${state.emailFilterStatus === filter.key ? 'active' : ''}" 
                                        data-filter="emails-status"
                                        data-value="${filter.key}"
                                        type="button"
                                    >
                                        ${escapeHtml(filter.label)}
                                    </button>
                                `).join('')}
                            </div>
                        </div>
                        <div class="ad-filter">
                            <span class="ad-filter-label">Service</span>
                            <div class="admin-filter-pills">
                                ${serviceFilters.map(filter => `
                                    <button 
                                        class="admin-filter-pill ${state.emailFilterService === filter.key ? 'active' : ''}" 
                                        data-filter="emails-service"
                                        data-value="${filter.key}"
                                        type="button"
                                    >
                                        ${escapeHtml(filter.label)}
                                    </button>
                                `).join('')}
                            </div>
                        </div>
                    </div>
                </div>
                <div class="ad-toolbar">
                    <div class="admin-filter-group ad-filter-grow">
                        <label for="admin-emails-search-to">Rechercher par destinataire</label>
                        <div class="cmd-center-search-wrapper">
                            <svg class="cmd-center-search-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                <circle cx="11" cy="11" r="8"></circle>
                                <path d="m21 21-4.35-4.35"></path>
                            </svg>
                            <input
                                type="text"
                                id="admin-emails-search-to"
                                class="cmd-center-search-input"
                                placeholder="Email du destinataire"
                                value="${escapeHtml(state.emailSearchTo || '')}"
                            />
                        </div>
                    </div>
                </div>
                ${emails.length === 0
                ? renderAdminEmptyState('emails')
                : `
                    <div class="admin-table-wrapper-modern">
                        <table class="admin-table-modern">
                            <thead>
                                <tr>
                                    <th>Type</th>
                                    <th>Sujet</th>
                                    <th>Destinataire</th>
                                    <th>Statut</th>
                                    <th>Service</th>
                                    <th>Date</th>
                                </tr>
                            </thead>
                            <tbody>
                                ${emails.map((email) => renderEmailRow(email)).join('')}
                            </tbody>
                        </table>
                    </div>
                `
            }
                ${renderPagination('emails', state.emailsPage, state.emailsTotalPages, state.emailsTotal, state.emailsPageSize)}
            </section>
        `;
    }

    function renderLifecycleTab() {
        if (state.lifecycleLoading && !state.lifecycle) {
            return `
                <section class="admin-section">
                    <p class="admin-empty">Chargement des parcours...</p>
                </section>
            `;
        }
        if (state.lifecycleError) {
            return `
                <section class="admin-section">
                    <div class="admin-error">
                        <p>${escapeHtml(state.lifecycleError)}</p>
                    </div>
                </section>
            `;
        }

        const data = state.lifecycle || { journeys: [], optOuts: {} };
        const journeys = data.journeys || [];
        const sum = (key) => journeys.reduce((total, journey) => total + (journey[key] || 0), 0);
        const channelLabels = { email: 'E-mail', push: 'Push' };

        const alerts = [];
        if (!data.sendingEnabled) {
            alerts.push(
                'Mode simulation : aucun message n’est envoyé tant que <code>LIFECYCLE_ENABLED=true</code> n’est pas défini côté API. Le cron calcule l’audience et la journalise.'
            );
        }
        if (!data.emailTemplateConfigured) {
            alerts.push(
                'Template e-mail « Parcours » non configuré (<code>SENDGRID_TEMPLATE_LIFECYCLE</code>) : seuls les push partent. Voir <code>email-templates/17-lifecycle.html</code>.'
            );
        }

        const kpis = [
            { icon: 'send', label: 'Envoyés (7 jours)', value: sum('sent7'), hint: 'Tous parcours' },
            { icon: 'outgoing_mail', tone: 'success', label: 'Envoyés (30 jours)', value: sum('sent30'), hint: 'Tous parcours' },
            { icon: 'error', tone: 'danger', label: 'Échecs (30 jours)', value: sum('failed30'), hint: 'Aucun canal abouti' },
            { icon: 'unsubscribe', tone: 'warning', label: 'Désinscrits e-mail', value: data.optOuts?.email ?? 0, hint: 'Conseils par e-mail' },
            { icon: 'notifications_off', label: 'Push désactivés', value: data.optOuts?.push ?? 0, hint: 'Conseils et rappels' },
            { icon: 'schedule', label: 'Écart minimal', value: `${data.minGapHours ?? 48} h`, hint: 'Entre deux messages' },
        ];

        const repeatLabel = (days) => (days == null ? 'Une fois' : `Tous les ${days} j max`);

        const journeyRows = journeys
            .map(
                (journey) => `
                <tr>
                    <td>
                        <div class="ad-stack">
                            <span class="ad-cell-strong">${escapeHtml(journey.label)}</span>
                            <span class="ad-sub">${escapeHtml(journey.description)}</span>
                        </div>
                    </td>
                    <td>
                        <div class="ad-stack">
                            ${pill(journey.trigger === 'event' ? 'Événement' : 'Planifié', journey.trigger === 'event' ? 'accent' : 'info', { dot: false })}
                            <span class="ad-sub">${escapeHtml(repeatLabel(journey.repeatAfterDays))}</span>
                        </div>
                    </td>
                    <td><span class="ad-lifecycle-channels">${journey.channels.map((channel) => pill(channelLabels[channel] || channel, 'neutral', { dot: false })).join('')}</span></td>
                    <td>${pill(journey.enabled ? 'Actif' : 'Désactivé', journey.enabled ? 'success' : 'neutral')}</td>
                    <td class="ad-cell-num">
                        <div class="ad-stack">
                            <span class="ad-cell-strong">${journey.sent7} · ${journey.sent30}</span>
                            ${journey.failed30 ? `<span class="ad-lifecycle-failed">${journey.failed30} échec(s)</span>` : ''}
                        </div>
                    </td>
                    <td class="ad-cell-actions">
                        <button class="rd-icon-btn" data-lifecycle-test="${escapeHtml(journey.key)}" type="button" ${state.lifecycleTesting ? 'disabled' : ''} title="Tester sur moi : envoyer « ${escapeHtml(journey.label)} » sur votre compte" aria-label="Tester « ${escapeHtml(journey.label)} » sur mon compte">
                            <span class="material-symbols-outlined">${state.lifecycleTesting === journey.key ? 'hourglass_top' : 'forward_to_inbox'}</span>
                        </button>
                    </td>
                </tr>
            `
            )
            .join('');

        return `
            <section class="admin-section">
                ${alerts.map((text) => `<div class="ad-alert ad-alert--warning">${text}</div>`).join('')}
                ${state.lifecycleNotice ? `<div class="ad-alert ad-alert--${state.lifecycleNotice.tone}" role="status">${escapeHtml(state.lifecycleNotice.text)}</div>` : ''}
                <div class="ad-kpis">
                    ${kpis
                        .map(
                            (card) => `
                        <div class="ad-kpi${card.tone ? ` ad-kpi--${card.tone}` : ''}">
                            <div class="ad-kpi-head">
                                <span class="ad-kpi-icon material-symbols-outlined" aria-hidden="true">${card.icon}</span>
                                <span class="ad-kpi-label">${escapeHtml(card.label)}</span>
                            </div>
                            <strong class="ad-kpi-value">${escapeHtml(String(card.value))}</strong>
                            <span class="ad-kpi-hint">${escapeHtml(card.hint)}</span>
                        </div>
                    `
                        )
                        .join('')}
                </div>

                <h2 class="ad-section-title ad-lifecycle-title">Parcours (par ordre de priorité)</h2>
                <div class="admin-table-wrapper-modern">
                    <table class="admin-table-modern">
                        <thead>
                            <tr>
                                <th>Parcours</th>
                                <th>Déclencheur</th>
                                <th>Canaux</th>
                                <th>Statut</th>
                                <th title="Messages envoyés sur 7 jours · 30 jours">Envois 7 j · 30 j</th>
                                <th><span class="ad-sr-only">Tester</span></th>
                            </tr>
                        </thead>
                        <tbody>${journeyRows}</tbody>
                    </table>
                </div>

                ${renderLifecycleRun()}
            </section>
        `;
    }

    function renderLifecycleRun() {
        const run = state.lifecycleRun;
        if (!run) {
            return `
                <h2 class="ad-section-title ad-lifecycle-title">Prochain passage</h2>
                <p class="ad-lifecycle-hint">« Simuler » calcule qui recevrait quoi au prochain passage du cron (toutes les heures, 10h–18h), sans rien envoyer.</p>
            `;
        }
        const entries = Object.entries(run.byJourney || {});
        const total = entries.reduce((sum, [, count]) => sum + count, 0);
        const channelLabels = { email: 'E-mail', push: 'Push' };
        return `
            <h2 class="ad-section-title ad-lifecycle-title">
                ${run.dryRun ? 'Simulation' : 'Dernière exécution'} · ${total} destinataire(s) sur ${run.evaluated} utilisateur(s) évalué(s)
            </h2>
            <p class="ad-lifecycle-hint">
                ${run.throttled} utilisateur(s) contacté(s) il y a moins de ${state.lifecycle?.minGapHours ?? 48} h, ignoré(s).
                ${run.truncated ? ' Passage interrompu par la limite d’envois (LIFECYCLE_MAX_PER_RUN).' : ''}
            </p>
            ${entries.length
                ? `<div class="ad-lifecycle-counts">${entries
                      .map(([key, count]) => pill(`${lifecycleJourneyLabel(key)} · ${count}`, 'info', { dot: false }))
                      .join('')}</div>`
                : ''}
            ${run.preview?.length
                ? `
                <div class="admin-table-wrapper-modern">
                    <table class="admin-table-modern">
                        <thead>
                            <tr>
                                <th>Destinataire</th>
                                <th>Parcours</th>
                                <th>Canaux</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${run.preview
                                .map(
                                    (row) => `
                                <tr>
                                    <td><div class="ad-ellipsis" title="${escapeHtml(row.email)}">${escapeHtml(row.email)}</div></td>
                                    <td>${escapeHtml(lifecycleJourneyLabel(row.journey))}</td>
                                    <td class="ad-cell-muted">${row.channels.map((channel) => escapeHtml(channelLabels[channel] || channel)).join(' + ')}</td>
                                </tr>
                            `
                                )
                                .join('')}
                        </tbody>
                    </table>
                </div>
                ${total > run.preview.length ? `<p class="ad-lifecycle-hint">Aperçu limité aux ${run.preview.length} premiers destinataires.</p>` : ''}
            `
                : '<p class="ad-lifecycle-hint">Personne n’est concerné pour l’instant.</p>'}
        `;
    }

    function renderTemplatesTab() {
        if (state.emailTemplatesLoading && state.emailTemplates.length === 0) {
            return `
                <section class="admin-section">
                    <p class="admin-empty">Chargement des templates d'emails...</p>
                </section>
            `;
        }

        if (state.emailTemplatesError) {
            return `
                <section class="admin-section">
                    <div class="admin-error">
                        <p>${escapeHtml(state.emailTemplatesError)}</p>
                    </div>
                </section>
            `;
        }

        const templates = state.emailTemplates || [];

        // Grouper les templates par catégorie
        const categories = {};
        templates.forEach(template => {
            if (!categories[template.category]) {
                categories[template.category] = [];
            }
            categories[template.category].push(template);
        });

        return `
            <section class="admin-section">
                ${state.templateSendForm.visible ? renderTemplateSendForm() : ''}

                ${Object.entries(categories).map(([category, categoryTemplates]) => `
                    <div class="ad-template-group">
                        <h2 class="ad-section-title">${escapeHtml(category)}</h2>
                        <div class="ad-template-grid">
                            ${categoryTemplates.map(template => renderTemplateCard(template)).join('')}
                        </div>
                    </div>
                `).join('')}
            </section>
        `;
    }

    function renderTemplateCard(template) {
        return `
            <div class="template-card ad-template-card" data-template-id="${template.id}">
                <div class="ad-template-head">
                    <span class="ad-template-icon material-symbols-outlined" aria-hidden="true">${templateIcon(template)}</span>
                    <div class="ad-template-text">
                        <h3 class="ad-template-name">${escapeHtml(template.name)}</h3>
                        <p class="ad-template-desc">${escapeHtml(template.description)}</p>
                    </div>
                </div>
                <dl class="ad-template-meta">
                    <div><dt>Sujet</dt><dd>${escapeHtml(template.exampleSubject)}</dd></div>
                    <div><dt>Contenu</dt><dd>${escapeHtml(template.exampleContent)}</dd></div>
                </dl>
                <button class="send-template-btn rd-btn rd-btn-light" data-template-id="${template.id}" type="button">
                    <span class="material-symbols-outlined">send</span>
                    Envoyer ce modèle
                </button>
            </div>
        `;
    }

    function renderTemplateSendForm() {
        const form = state.templateSendForm;
        const template = state.selectedTemplate;

        if (!template) {
            return '';
        }

        const disabled = form.sending ? 'disabled' : '';
        const labelFor = (field, fieldId) => `
            <label for="${fieldId}" class="ad-label">
                ${escapeHtml(field.label)}${field.required ? ' <span class="ad-required">*</span>' : ''}
            </label>
        `;

        return `
            <div class="ad-form-card">
                <div class="ad-form-head">
                    <span class="ad-template-icon material-symbols-outlined" aria-hidden="true">${templateIcon(template)}</span>
                    <div class="ad-form-heading">
                        <h3 class="ad-form-title">${escapeHtml(template.name)}</h3>
                        <p class="ad-form-sub">${escapeHtml(template.description)}</p>
                    </div>
                    <button id="close-template-send-form" class="rd-icon-btn" type="button" title="Fermer" aria-label="Fermer">
                        <span class="material-symbols-outlined">close</span>
                    </button>
                </div>

                ${form.error ? `<div class="ad-alert ad-alert--danger">${escapeHtml(form.error)}</div>` : ''}
                ${form.success ? '<div class="ad-alert ad-alert--success">Email envoyé.</div>' : ''}

                <form id="template-send-form" class="ad-form">
                    ${template.fields.map(field => {
                        const fieldId = `template-field-${field.name}`;
                        const fieldValue = form.data[field.name] || '';

                        if (field.type === 'textarea') {
                            return `
                                <div class="ad-field">
                                    ${labelFor(field, fieldId)}
                                    <textarea id="${fieldId}" name="${field.name}" class="ad-input ad-textarea" placeholder="${escapeHtml(field.label)}" ${field.required ? 'required' : ''} rows="4" ${disabled}>${escapeHtml(fieldValue)}</textarea>
                                </div>
                            `;
                        } else if (field.type === 'select') {
                            return `
                                <div class="ad-field">
                                    ${labelFor(field, fieldId)}
                                    <select id="${fieldId}" name="${field.name}" class="ad-input" ${field.required ? 'required' : ''} ${disabled}>
                                        <option value="">Sélectionner…</option>
                                        ${(field.options || []).map(opt => `
                                            <option value="${opt}" ${fieldValue === opt ? 'selected' : ''}>${escapeHtml(opt)}</option>
                                        `).join('')}
                                    </select>
                                </div>
                            `;
                        } else if (field.type === 'user-select') {
                            return `
                                <div class="ad-field ad-field--suggest">
                                    ${labelFor(field, fieldId)}
                                    <div class="ad-input-wrap">
                                        <span class="material-symbols-outlined ad-input-icon" aria-hidden="true">person_search</span>
                                        <input type="email" id="${fieldId}" name="${field.name}" class="ad-input" value="${escapeHtml(fieldValue)}" placeholder="Email ou nom d'un utilisateur" ${field.required ? 'required' : ''} autocomplete="off" ${disabled} />
                                    </div>
                                    <div id="${fieldId}-suggestions" class="user-suggestions ad-suggestions" style="display: none;"></div>
                                </div>
                            `;
                        }
                        return `
                            <div class="ad-field">
                                ${labelFor(field, fieldId)}
                                <input type="${field.type}" id="${fieldId}" name="${field.name}" class="ad-input" value="${escapeHtml(fieldValue)}" placeholder="${escapeHtml(field.label)}" ${field.required ? 'required' : ''} ${disabled} />
                            </div>
                        `;
                    }).join('')}

                    <div class="ad-form-foot">
                        <button type="button" id="cancel-template-send" class="rd-btn rd-btn-light" ${disabled}>Annuler</button>
                        <button type="submit" class="rd-btn rd-btn-night" ${disabled}>
                            <span class="material-symbols-outlined">${form.sending ? 'hourglass_top' : 'send'}</span>
                            ${form.sending ? 'Envoi…' : 'Envoyer'}
                        </button>
                    </div>
                </form>
            </div>
        `;
    }

    function renderSupportTab() {
        if (state.supportLoading && state.supportContacts.length === 0) {
            return `
                <section class="admin-section">
                    <p class="admin-empty">Chargement des contacts support...</p>
                </section>
            `;
        }

        if (state.supportError) {
            return `
                <section class="admin-section">
                    <div class="admin-error">
                        <p>${escapeHtml(state.supportError)}</p>
                    </div>
                </section>
            `;
        }

        const contacts = state.supportContacts || [];
        const statusLabels = {
            new: 'Nouveau',
            acknowledged: 'Pris en charge',
            waiting_user: 'Attente utilisateur',
            waiting_support: 'Attente support',
            resolved: 'Résolu',
            closed: 'Fermé',
        };

        const contactsMarkup =
            contacts.length === 0
                ? renderAdminEmptyState('support')
                : `
                    <div class="admin-table-wrapper-modern">
                        <table class="admin-table-modern">
                            <thead>
                                <tr>
                                    <th>Sujet</th>
                                    <th>Statut</th>
                                    <th>Utilisateur</th>
                                    <th>Date</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                ${contacts.map((contact) => renderSupportRow(contact, statusLabels)).join('')}
                            </tbody>
                        </table>
                    </div>
            `;

        const statusOptions = [
            { key: '', label: 'Tous les statuts' },
            { key: 'pending', label: 'En attente' },
            { key: 'in_progress', label: 'En cours' },
            { key: 'replied', label: 'Support a répondu' },
            { key: 'waiting_response', label: 'En attente réponse' },
            { key: 'resolved', label: 'Résolu' },
            { key: 'closed', label: 'Fermé' },
        ];

        return `
            <section class="admin-section">
                <div class="ad-toolbar">
                    <div class="admin-filter-pills">
                        ${statusOptions
                .map(
                    (option) => `
                            <button 
                                class="admin-filter-pill ${state.supportFilterStatus === option.key ? 'active' : ''}" 
                                data-filter="support-status"
                                data-value="${option.key}"
                                type="button"
                            >
                                ${escapeHtml(option.label)}
                            </button>
                        `
                )
                .join('')}
                    </div>
                </div>
                ${contactsMarkup}
                ${renderPagination('support', state.supportPage, state.supportTotalPages, state.supportTotal, state.supportPageSize)}
            </section>
        `;
    }

    function renderAdminEmptyState(type) {
        // Mapping des types admin vers les presets
        const presetMapping = {
            support: EmptyStatePresets.adminSupport,
            executions: EmptyStatePresets.adminExecutions,
            events: EmptyStatePresets.adminEvents,
            requests: EmptyStatePresets.adminRequests,
            users: EmptyStatePresets.adminUsers,
            errors: EmptyStatePresets.adminErrors,
            emails: EmptyStatePresets.adminEmails,
        };

        const preset = presetMapping[type] || {
            title: 'Aucune donnée',
            message: 'Aucune donnée disponible pour le moment.',
            icon: 'info',
            variant: 'default',
        };

        return renderEmptyStateHTML({
            ...preset,
            frameless: true,
        });
    }

    function renderPagination(type, currentPage, totalPages, totalItems, pageSize = PAGE_SIZE) {
        // Utiliser le format scénario pour tous les types
        const itemLabels = {
            'support': { singular: 'contact', plural: 'contacts' },
            'executions': { singular: 'exécution', plural: 'exécutions' },
            'events': { singular: 'événement', plural: 'événements' },
            'users': { singular: 'utilisateur', plural: 'utilisateurs' },
            'requests': { singular: 'demande', plural: 'demandes' },
            'errors': { singular: 'erreur', plural: 'erreurs' },
            'emails': { singular: 'email', plural: 'emails' }
        };

        const labels = itemLabels[type] || { singular: 'élément', plural: 'éléments' };
        const itemLabel = labels.singular;
        const itemLabelPlural = labels.plural;

        // Afficher la pagination si on a plus d'une page ou si on a des éléments (comme dans les scénarios)
        if (totalPages > 1 || totalItems > 0) {
            return `
                <div class="scenario-pagination-modern" data-pagination="${type}">
                    <button class="scenario-pagination-btn pagination-prev" ${currentPage <= 1 ? 'disabled' : ''}>← Précédent</button>
                    <span class="pagination-info">Page ${currentPage} sur ${totalPages} (${totalItems} ${totalItems > 1 ? itemLabelPlural : itemLabel})</span>
                    <button class="scenario-pagination-btn pagination-next" ${currentPage >= totalPages ? 'disabled' : ''}>Suivant →</button>
                </div>
            `;
        }
        return '';
    }

    function getActorLabel(actor) {
        if (!actor) return '—';
        const name = actor.name?.trim();
        const email = actor.email?.trim();
        if (name && email) {
            return `${name} (${email})`;
        }
        return name || email || '—';
    }

    function getEntityLabel(entity) {
        if (!entity) return '—';
        const name = entity.name?.trim();
        const type = entity.type?.trim();
        if (name && type) {
            return `${name} · ${type}`;
        }
        return name || type || '—';
    }

    function formatMetricValue(value) {
        const numeric = Number(value ?? 0);
        if (Number.isFinite(numeric)) {
            return numeric.toLocaleString('fr-FR');
        }
        return String(value ?? 0);
    }

    function formatDurationLabel(durationMs) {
        const ms = Number(durationMs);
        if (!Number.isFinite(ms) || ms <= 0) {
            return '—';
        }
        if (ms < 1000) {
            return `${Math.round(ms)} ms`;
        }
        const secondsTotal = ms / 1000;
        if (secondsTotal < 60) {
            return `${secondsTotal.toFixed(secondsTotal >= 10 ? 1 : 2)} s`;
        }
        const minutes = Math.floor(secondsTotal / 60);
        const seconds = Math.round(secondsTotal % 60);
        return `${minutes} min ${seconds.toString().padStart(2, '0')} s`;
    }

    function renderEventRow(event) {
        const eventType = event.type || '—';

        return `
            <tr data-event-id="${event._id || event.id}" class="admin-table-row-clickable" data-type="event">
                <td>${pill(eventType, toneFor('event', eventType), { mono: true })}</td>
                <td>${escapeHtml(event.description || '—')}</td>
                <td class="ad-cell-muted">${escapeHtml(getActorLabel(event.actor))}</td>
                <td class="ad-cell-muted">${escapeHtml(getEntityLabel(event.entity))}</td>
                <td class="ad-cell-muted ad-cell-date">${escapeHtml(formatDateTime(event.createdAt))}</td>
            </tr>
        `;
    }

    function renderRequestRow(request) {
        const statusLabel = REQUEST_STATUSES.find(s => s.value === request.status)?.label || request.status || '—';
        const typeLabel = request.type === 'application' ? 'Application' : request.type === 'command' ? 'Commande' : request.type === 'roadmap' ? 'Suggestion' : request.type || '—';
        const requestType = request.type || 'application';
        const requestName = request.name || request.title || '—';
        const applicationName = request.applicationName || '';

        let nameCell = '';
        if (requestType === 'application') {
            const appName = applicationName || requestName;
            nameCell = `<span class="ad-cell-strong">${escapeHtml(appName)}</span>`;
        } else if (requestType === 'command') {
            nameCell = `
                <div class="ad-stack">
                    <span class="ad-cell-strong">${escapeHtml(requestName)}</span>
                    ${applicationName ? `<span class="ad-sub"><span class="material-symbols-outlined" aria-hidden="true">apps</span>${escapeHtml(applicationName)}</span>` : ''}
                </div>
            `;
        } else {
            nameCell = `<span class="ad-cell-strong">${escapeHtml(requestName)}</span>`;
        }

        const isCompleted = request.status === 'completed';
        return `
            <tr data-request-id="${request._id || request.id}" class="admin-table-row-clickable${isCompleted ? ' is-completed' : ''}" data-type="request">
                <td><span class="ad-type ad-type--${escapeHtml(requestType)}">${escapeHtml(typeLabel)}</span></td>
                <td>${nameCell}</td>
                <td>${pill(statusLabel, toneFor('request', request.status))}</td>
                <td>${renderUserCell(request.userName, request.userEmail, request.userUsername)}</td>
                <td class="ad-cell-muted ad-cell-date">${formatDateTime(request.createdAt)}</td>
                <td class="ad-cell-actions">
                    <button class="delete-request-btn rd-icon-btn ad-icon-danger" data-request-id="${request._id || request.id}" title="Supprimer la demande" aria-label="Supprimer la demande" type="button">
                        <span class="material-symbols-outlined">delete</span>
                    </button>
                </td>
            </tr>
        `;
    }

    function renderUserCell(name, email, username) {
        if (!name && !email && !username) {
            return '<span class="ad-muted">—</span>';
        }
        return `
            <div class="ad-stack">
                ${name ? `<span class="ad-cell-strong">${escapeHtml(name)}</span>` : ''}
                ${email ? `<span class="ad-sub">${escapeHtml(email)}</span>` : ''}
                ${username ? `<code class="admin-user-id">${escapeHtml(username)}</code>` : ''}
            </div>
        `;
    }

    function renderSupportRow(contact, statusLabels) {
        const isClosed = contact.status === 'closed';

        // Utiliser les données peuplées de userId si disponibles, sinon utiliser les données directes
        const userIdData = contact.userId && typeof contact.userId === 'object' ? contact.userId : null;
        const userName = contact.userName || userIdData?.name || '';
        const userEmail = contact.userEmail || userIdData?.email || '';
        const userUsername = userIdData?.username || '';

        return `
            <tr data-support-id="${contact.id}" class="admin-table-row-clickable" data-type="support">
                <td class="ad-cell-strong">${escapeHtml(contact.subject || 'Sans sujet')}</td>
                <td>${pill(statusLabels[contact.status] || contact.status, toneFor('support', contact.status))}</td>
                <td>${renderUserCell(userName, userEmail, userUsername)}</td>
                <td class="ad-cell-muted ad-cell-date">${formatDateTime(contact.createdAt)}</td>
                <td class="ad-cell-actions">
                    <div class="ad-row-actions">
                        <button class="view-support-btn rd-icon-btn" data-support-id="${contact.id}" title="Voir les détails" aria-label="Voir les détails" type="button">
                            <span class="material-symbols-outlined">visibility</span>
                        </button>
                        ${!isClosed ? `
                            <button class="close-support-btn rd-icon-btn ad-icon-warning" data-support-id="${contact.id}" title="Clôturer le ticket" aria-label="Clôturer le ticket" type="button">
                                <span class="material-symbols-outlined">lock</span>
                            </button>
                        ` : ''}
                        <button class="delete-support-btn rd-icon-btn ad-icon-danger" data-support-id="${contact.id}" title="Supprimer le ticket" aria-label="Supprimer le ticket" type="button">
                            <span class="material-symbols-outlined">delete</span>
                        </button>
                    </div>
                </td>
            </tr>
        `;
    }

    function renderExecutionLogs(logs = []) {
        if (!Array.isArray(logs) || logs.length === 0) {
            return `
                <section class="admin-execution-section">
                    <button class="btn-secondary admin-logs-btn" type="button" disabled>
                        <span class="material-symbols-outlined">receipt_long</span> Logs
                    </button>
                    <p class="admin-empty" style="margin-top: 1rem;">Aucun log enregistré.</p>
                </section>
            `;
        }

        const items = logs
            .map((log) => {
                const metaParts = [];
                if (log?.applicationName) {
                    metaParts.push(log.applicationName);
                }
                if (typeof log?.commandIndex === 'number') {
                    metaParts.push(`cmd #${log.commandIndex + 1}`);
                }
                return `
                <li class="admin-log-item level-${escapeHtml(log?.level || 'info')}">
                    <span class="admin-log-time">${escapeHtml(formatDateTime(log?.timestamp))}</span>
                    <span class="admin-log-level">${escapeHtml((log?.level || 'info').toUpperCase())}</span>
                    <span class="admin-log-message">${escapeHtml(log?.message || '')}</span>
                    ${metaParts.length ? `<span class="admin-log-meta">${escapeHtml(metaParts.join(' • '))}</span>` : ''}
                </li>
            `;
            })
            .join('');

        return `
            <section class="admin-execution-section">
                <button class="btn-secondary admin-logs-btn" type="button">
                    <span class="material-symbols-outlined">receipt_long</span> Logs (${logs.length})
                </button>
                <ul class="admin-log-list" style="margin-top: 1rem;">
                    ${items}
                </ul>
            </section>
        `;
    }

    // La sidebar est statique (hors de .admin-content) : on la lie une seule fois,
    // sinon chaque render empilait un listener de plus sur chaque bouton.
    function bindSidebarNav() {
        const sidebarButtons = container.querySelectorAll('.admin-nav-item');
        sidebarButtons.forEach((btn) => {
            btn.addEventListener('click', () => {
                const tab = btn.dataset.tab || 'users';
                if (state.currentTab !== tab) {
                    state.currentTab = tab;
                    if (tab === 'overview' && !state.overview && !state.overviewLoading) {
                        loadOverview(true);
                    }
                    if (tab === 'events' && !state.eventsLoading && state.events.length === 0) {
                        loadEvents({ page: state.eventsPage, showSpinner: true });
                    }
                    if (tab === 'templates' && !state.emailTemplatesLoading && state.emailTemplates.length === 0) {
                        loadEmailTemplates({ showSpinner: true });
                    }
                    if (
                        tab === 'executions' &&
                        !state.executionsLoading &&
                        state.executions.length === 0
                    ) {
                        loadExecutions({ page: state.executionsPage, showSpinner: true });
                    }
                    if (tab === 'users' && !state.usersLoading && state.users.length === 0) {
                        loadUsers({ page: state.usersPage, showSpinner: true });
                    }
                    if (
                        tab === 'requests' &&
                        !state.requestsLoading &&
                        state.requests.length === 0
                    ) {
                        loadRequests({ page: state.requestsPage, showSpinner: true });
                    }
                    if (
                        tab === 'support' &&
                        !state.supportLoading &&
                        state.supportContacts.length === 0
                    ) {
                        loadSupportContacts({ page: state.supportPage, showSpinner: true });
                    }
                    if (tab === 'errors' && !state.errorsLoading && state.errors.length === 0) {
                        loadErrors({ page: state.errorsPage, showSpinner: true });
                    }
                    if (tab === 'emails' && !state.emailsLoading && state.emails.length === 0) {
                        loadEmails({ page: state.emailsPage, showSpinner: true });
                    }
                    if (tab === 'lifecycle' && !state.lifecycleLoading) {
                        loadLifecycle({ showSpinner: !state.lifecycle });
                    }
                    render();
                }
            });
        });
    }

    // Fermer la liste d'autocomplete utilisateur (journal d'activité) quand on clique ailleurs.
    // Un seul listener par instance (l'ancien était global à window et gardait l'état de la 1re instance).
    function bindEventsAutocompleteOutsideClick() {
        document.addEventListener('click', (event) => {
            if (state.currentTab !== 'events' || eventsUsersList.length === 0) return;
            if (!(event.target instanceof Element)) return;
            if (
                event.target.closest('.admin-filter-group-user') ||
                event.target.closest('.admin-users-autocomplete-list')
            ) {
                return;
            }
            eventsUsersList = [];
            render();
        });
    }

    function bindTabInteractions(content) {
        content.querySelectorAll('[data-lifecycle-run]').forEach((btn) => {
            btn.addEventListener('click', () => {
                const send = btn.dataset.lifecycleRun === 'send';
                if (send && !window.confirm('Envoyer maintenant les messages de parcours à tous les destinataires éligibles ?')) {
                    return;
                }
                runLifecycle({ dryRun: !send });
            });
        });
        content.querySelectorAll('[data-lifecycle-test]').forEach((btn) => {
            btn.addEventListener('click', () => testLifecycleJourney(btn.dataset.lifecycleTest));
        });

        content.querySelectorAll('.admin-refresh').forEach((btn) => {
            btn.addEventListener('click', () => {
                const target = btn.dataset.target;
                switch (target) {
                    case 'events':
                        loadEvents({ page: 1, showSpinner: true });
                        break;
                    case 'errors':
                        loadErrors({ page: 1, showSpinner: true });
                        break;
                    default:
                        loadData(true);
                }
            });
        });

        // Gestion des filter pills pour les demandes (requests)
        content.querySelectorAll('[data-filter="requests-type"]').forEach((pill) => {
            pill.addEventListener('click', () => {
                const filterType = pill.dataset.value || '';
                state.filterType = filterType;
                // Mettre à jour l'état actif
                content.querySelectorAll('[data-filter="requests-type"]').forEach((p) => {
                    p.classList.remove('active');
                });
                pill.classList.add('active');
                state.requestsPage = 1;
                loadRequests({ page: 1, showSpinner: true });
            });
        });

        content.querySelectorAll('[data-filter="requests-status"]').forEach((pill) => {
            pill.addEventListener('click', () => {
                const filterStatus = pill.dataset.value || '';
                state.filterStatus = filterStatus;
                // Mettre à jour l'état actif
                content.querySelectorAll('[data-filter="requests-status"]').forEach((p) => {
                    p.classList.remove('active');
                });
                pill.classList.add('active');
                state.requestsPage = 1;
                loadRequests({ page: 1, showSpinner: true });
            });
        });

        // Gestion des filter pills pour le type d'événement
        content.querySelectorAll('[data-filter="events-type"]').forEach((pill) => {
            pill.addEventListener('click', () => {
                const filterType = pill.dataset.value || '';
                state.eventFilterType = filterType;
                // Mettre à jour l'état actif
                content.querySelectorAll('[data-filter="events-type"]').forEach((p) => {
                    p.classList.remove('active');
                });
                pill.classList.add('active');
                state.eventsPage = 1;
                loadEvents({ page: 1, showSpinner: true });
            });
        });

        // Gestion de la recherche d'utilisateur pour les événements
        const eventUserInput = content.querySelector('#admin-event-user');
        if (eventUserInput) {
            eventUserInput.addEventListener('input', (event) => {
                const searchValue = event.target.value.trim();
                state.eventFilterUserSearch = searchValue;
                // Nouvelle saisie après avoir choisi un utilisateur : on repasse en mode recherche,
                // sinon la liste de suggestions ne se réaffiche jamais (isSearching exige !eventFilterUser).
                if (state.eventFilterUser && searchValue !== state.eventFilterUser) {
                    state.eventFilterUser = '';
                }

                // Si la valeur correspond à un ID valide, l'utiliser directement
                if (searchValue && /^[0-9a-fA-F]{24}$/.test(searchValue)) {
                    state.eventFilterUser = searchValue;
                    eventsUsersList = [];
                    state.eventsPage = 1;
                    loadEvents({ page: 1, showSpinner: true });
                    return;
                }

                // Si le champ est vide, réinitialiser le filtre
                if (!searchValue) {
                    state.eventFilterUser = '';
                    eventsUsersList = [];
                    state.eventsPage = 1;
                    loadEvents({ page: 1, showSpinner: true });
                    return;
                }

                // Annuler le timeout précédent
                if (eventsUserSearchTimeout) {
                    clearTimeout(eventsUserSearchTimeout);
                }

                // Rechercher les utilisateurs avec debounce
                if (searchValue.length >= 2) {
                    eventsUserSearchTimeout = setTimeout(() => {
                        searchEventsUsers(searchValue);
                    }, 300);
                } else {
                    eventsUsersList = [];
                    // Ne pas faire de render ici pour éviter de perdre le focus
                    // La liste sera mise à jour lors du prochain render
                }
            });

            // Clic sur un utilisateur de la liste. Délégation sur la liste elle-même (recréée à chaque
            // render) : lié sur `content`, qui persiste, le listener s'empilait à chaque render.
            const autocompleteList = content.querySelector('.admin-users-autocomplete-list');
            autocompleteList?.addEventListener('click', (event) => {
                const item = event.target.closest('.admin-users-autocomplete-item');
                if (!item) return;
                const userId = item.dataset.userId;
                const username = item.dataset.userUsername || userId;
                state.eventFilterUser = userId;
                state.eventFilterUserSearch = username;
                eventsUsersList = [];
                eventsUsersLoading = false;
                state.eventsPage = 1;
                loadEvents({ page: 1, showSpinner: true });
            });
        }

        // Gestion des filter pills pour les exécutions
        content.querySelectorAll('[data-filter="executions-status"]').forEach((pill) => {
            pill.addEventListener('click', () => {
                const filterStatus = pill.dataset.value || '';
                state.executionFilterStatus = filterStatus;
                // Mettre à jour l'état actif
                content.querySelectorAll('[data-filter="executions-status"]').forEach((p) => {
                    p.classList.remove('active');
                });
                pill.classList.add('active');
                state.executionsPage = 1;
                loadExecutions({ page: 1, showSpinner: true });
            });
        });

        // Gestion des filter pills pour le support
        content.querySelectorAll('[data-filter="support-status"]').forEach((pill) => {
            pill.addEventListener('click', () => {
                const filterStatus = pill.dataset.value || '';
                state.supportFilterStatus = filterStatus;
                // Mettre à jour l'état actif
                content.querySelectorAll('[data-filter="support-status"]').forEach((p) => {
                    p.classList.remove('active');
                });
                pill.classList.add('active');
                state.supportPage = 1;
                loadSupportContacts({ page: 1, showSpinner: true });
            });
        });

        // Gestion des filter pills pour les emails
        content.querySelectorAll('[data-filter="emails-type"]').forEach((pill) => {
            pill.addEventListener('click', () => {
                const filterType = pill.dataset.value || '';
                state.emailFilterType = filterType;
                content.querySelectorAll('[data-filter="emails-type"]').forEach((p) => {
                    p.classList.remove('active');
                });
                pill.classList.add('active');
                state.emailsPage = 1;
                loadEmails({ page: 1, showSpinner: true });
            });
        });

        content.querySelectorAll('[data-filter="emails-status"]').forEach((pill) => {
            pill.addEventListener('click', () => {
                const filterStatus = pill.dataset.value || '';
                state.emailFilterStatus = filterStatus;
                content.querySelectorAll('[data-filter="emails-status"]').forEach((p) => {
                    p.classList.remove('active');
                });
                pill.classList.add('active');
                state.emailsPage = 1;
                loadEmails({ page: 1, showSpinner: true });
            });
        });

        content.querySelectorAll('[data-filter="emails-service"]').forEach((pill) => {
            pill.addEventListener('click', () => {
                const filterService = pill.dataset.value || '';
                state.emailFilterService = filterService;
                content.querySelectorAll('[data-filter="emails-service"]').forEach((p) => {
                    p.classList.remove('active');
                });
                pill.classList.add('active');
                state.emailsPage = 1;
                loadEmails({ page: 1, showSpinner: true });
            });
        });

        // Gestion de la recherche par destinataire pour les emails
        const emailsSearchInput = content.querySelector('#admin-emails-search-to');
        if (emailsSearchInput) {
            emailsSearchInput.addEventListener('input', (e) => {
                const searchValue = e.target.value.trim();
                state.emailSearchTo = searchValue;

                if (emailsSearchTimeout) {
                    clearTimeout(emailsSearchTimeout);
                }

                emailsSearchTimeout = setTimeout(() => {
                    state.emailsPage = 1;
                    loadEmails({ page: 1, showSpinner: true });
                }, 500);
            });
        }

        // Gestion des cartes de templates
        const templateCards = content.querySelectorAll('.template-card');
        templateCards.forEach(card => {
            card.addEventListener('click', (e) => {
                // Ne pas ouvrir si on clique sur le bouton d'envoi
                if (e.target.closest('.send-template-btn')) {
                    return;
                }
                
                const templateId = card.dataset.templateId;
                const template = state.emailTemplates.find(t => t.id === templateId);
                if (template) {
                    log.info('Template card clicked', { templateId });
                    // On pourrait afficher un modal de prévisualisation ici
                }
            });
        });

        // Gestion des boutons d'envoi de template
        const sendTemplateBtns = content.querySelectorAll('.send-template-btn');
        sendTemplateBtns.forEach(btn => {
            btn.addEventListener('click', () => {
                const templateId = btn.dataset.templateId;
                const template = state.emailTemplates.find(t => t.id === templateId);
                if (template) {
                    state.selectedTemplate = template;
                    state.templateSendForm.visible = true;
                    state.templateSendForm.data = {};
                    state.templateSendForm.error = null;
                    state.templateSendForm.success = false;
                    render();
                    
                    // Scroll vers le formulaire
                    setTimeout(() => {
                        const form = document.querySelector('#template-send-form');
                        if (form) {
                            form.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }
                    }, 100);
                }
            });
        });

        // Gestion du formulaire d'envoi de template
        const templateSendForm = content.querySelector('#template-send-form');
        if (templateSendForm) {
            templateSendForm.addEventListener('submit', async (e) => {
                e.preventDefault();
                
                const formData = new FormData(templateSendForm);
                const data = {};
                for (const [key, value] of formData.entries()) {
                    data[key] = value.trim();
                }

                state.templateSendForm.sending = true;
                state.templateSendForm.error = null;
                state.templateSendForm.success = false;
                render();

                try {
                    // Vérifier que l'utilisateur est authentifié et admin
                    const currentUser = typeof getCurrentUser === 'function' ? getCurrentUser() : null;
                    if (!currentUser) {
                        throw new Error('Vous devez être connecté pour envoyer un email.');
                    }
                    if (currentUser.role !== 'admin') {
                        throw new Error('Seuls les administrateurs peuvent envoyer des emails.');
                    }
                    
                    // Pour le template custom, utiliser le templateType choisi par l'utilisateur
                    let templateId = state.selectedTemplate.id;
                    if (templateId === 'custom' && data.templateType) {
                        templateId = data.templateType === 'custom' ? 'admin_custom' : `admin_${data.templateType}`;
                        delete data.templateType; // Ne pas envoyer ce champ au backend
                    }
                    
                    log.info('Sending template email', { templateId, to: data.to, currentUserRole: currentUser.role });
                    
                    const response = await apiFetch('/api/admin/send-template', {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({
                            templateId,
                            data,
                        }),
                    });

                    if (!response.ok) {
                        const errorData = await response.json().catch(() => ({}));
                        const errorMessage = errorData.error || `Erreur HTTP ${response.status}: ${response.statusText}`;
                        log.error('Template send failed with HTTP error', { 
                            status: response.status, 
                            statusText: response.statusText,
                            errorData,
                            templateId
                        });
                        throw new Error(errorMessage);
                    }

                    state.templateSendForm.success = true;
                    state.templateSendForm.error = null;
                    state.templateSendForm.data = {};
                    
                    // Recharger la liste des emails après 2 secondes
                    setTimeout(() => {
                        if (state.currentTab === 'emails') {
                            loadEmails({ page: 1, showSpinner: false });
                        }
                        state.templateSendForm.visible = false;
                        state.templateSendForm.success = false;
                        state.selectedTemplate = null;
                        render();
                    }, 2000);

                    render();
                } catch (error) {
                    log.error('Failed to send template', error);
                    state.templateSendForm.error = error.message || 'Erreur lors de l\'envoi du template.';
                    state.templateSendForm.success = false;
                    render();
                } finally {
                    state.templateSendForm.sending = false;
                }
            });
        }

        const closeTemplateSendFormBtn = content.querySelector('#close-template-send-form');
        if (closeTemplateSendFormBtn) {
            closeTemplateSendFormBtn.addEventListener('click', () => {
                state.templateSendForm.visible = false;
                state.templateSendForm.data = {};
                state.templateSendForm.error = null;
                state.templateSendForm.success = false;
                state.selectedTemplate = null;
                render();
            });
        }

        // Gestion de la recherche d'utilisateurs pour le champ user-select
        const userSelectField = content.querySelector('#template-field-to');
        const userSuggestions = content.querySelector('#template-field-to-suggestions');
        
        if (userSelectField && userSuggestions) {
            let searchTimeout;
            
            userSelectField.addEventListener('input', async () => {
                const searchTerm = userSelectField.value.trim();
                
                if (searchTimeout) {
                    clearTimeout(searchTimeout);
                }
                
                if (searchTerm.length < 2) {
                    userSuggestions.style.display = 'none';
                    userSuggestions.innerHTML = '';
                    return;
                }
                
                searchTimeout = setTimeout(async () => {
                    try {
                        const response = await apiFetch(`/api/admin/users?search=${encodeURIComponent(searchTerm)}&limit=10`);
                        
                        if (!response.ok) {
                            throw new Error('Erreur lors de la recherche');
                        }
                        
                        const data = await response.json();
                        const users = data.users || [];
                        
                        if (users.length === 0) {
                            userSuggestions.innerHTML = `
                                <div class="ad-suggestion-empty">Aucun utilisateur trouvé</div>
                            `;
                            userSuggestions.style.display = 'block';
                            return;
                        }
                        
                        userSuggestions.innerHTML = users.map(user => `
                            <div 
                                class="user-suggestion-item"
                                data-email="${escapeHtml(user.email)}"
                                data-name="${escapeHtml(user.name || '')}"
                            >
                                <span class="ad-suggestion-email">${escapeHtml(user.email)}</span>
                                ${user.name ? `
                                    <span class="ad-suggestion-meta">${escapeHtml(user.name)} ${user.username ? `· @${escapeHtml(user.username)}` : ''}</span>
                                ` : ''}
                            </div>
                        `).join('');
                        
                        userSuggestions.style.display = 'block';
                        
                        // Ajouter les event listeners aux suggestions
                        userSuggestions.querySelectorAll('.user-suggestion-item').forEach(item => {
                            item.addEventListener('click', () => {
                                const email = item.dataset.email;
                                userSelectField.value = email;
                                userSuggestions.style.display = 'none';
                                userSuggestions.innerHTML = '';
                            });
                        });
                        
                    } catch (error) {
                        log.error('Failed to search users', error);
                        userSuggestions.innerHTML = `
                            <div class="ad-suggestion-empty ad-suggestion-error">Erreur lors de la recherche</div>
                        `;
                        userSuggestions.style.display = 'block';
                    }
                }, 300);
            });
            
            // Fermer les suggestions en cliquant ailleurs (on remplace le listener du render précédent
            // au lieu d'en empiler un nouveau sur document à chaque render)
            if (templateSuggestionsOutsideHandler) {
                document.removeEventListener('click', templateSuggestionsOutsideHandler);
            }
            templateSuggestionsOutsideHandler = (e) => {
                if (!userSelectField.contains(e.target) && !userSuggestions.contains(e.target)) {
                    userSuggestions.style.display = 'none';
                }
            };
            document.addEventListener('click', templateSuggestionsOutsideHandler);
        } else if (templateSuggestionsOutsideHandler) {
            document.removeEventListener('click', templateSuggestionsOutsideHandler);
            templateSuggestionsOutsideHandler = null;
        }

        const cancelTemplateSendBtn = content.querySelector('#cancel-template-send');
        if (cancelTemplateSendBtn) {
            cancelTemplateSendBtn.addEventListener('click', () => {
                state.templateSendForm.visible = false;
                state.templateSendForm.data = {};
                state.templateSendForm.error = null;
                state.templateSendForm.success = false;
                state.selectedTemplate = null;
                render();
            });
        }

        // Gestion du bouton "Email personnalisé"
        const showCustomEmailBtn = content.querySelector('#show-custom-email-form');
        if (showCustomEmailBtn) {
            showCustomEmailBtn.addEventListener('click', () => {
                // Créer un template personnalisé générique
                const customTemplate = {
                    id: 'custom',
                    name: 'Email personnalisé',
                    icon: 'edit_square',
                    description: 'Envoyer un email personnalisé avec votre propre contenu',
                    category: 'Personnalisé',
                    fields: [
                        { name: 'to', label: 'Destinataire', type: 'user-select', required: true },
                        { name: 'subject', label: 'Sujet', type: 'text', required: true },
                        { name: 'content', label: 'Contenu', type: 'textarea', required: true },
                        { 
                            name: 'templateType', 
                            label: 'Type de template', 
                            type: 'select', 
                            required: false,
                            options: ['custom', 'welcome', 'announcement', 'notification']
                        }
                    ],
                    exampleSubject: 'Votre sujet ici',
                    exampleContent: 'Votre contenu ici...'
                };
                
                state.selectedTemplate = customTemplate;
                state.templateSendForm.visible = true;
                state.templateSendForm.data = { templateType: 'custom' };
                state.templateSendForm.error = null;
                state.templateSendForm.success = false;
                render();
            });
        }

        // Ajouter les gestionnaires de clic sur les lignes de table
        const clickableRows = content.querySelectorAll('.admin-table-row-clickable');
        clickableRows.forEach((row) => {
            row.addEventListener('click', (e) => {
                // Ne pas ouvrir si on clique sur un bouton ou un lien
                if (e.target.closest('button') || e.target.closest('a')) {
                    return;
                }

                const type = row.dataset.type;
                let data = null;

                if (type === 'user') {
                    const userId = row.dataset.userId;
                    data = state.users.find(u => (u._id || u.id) === userId);
                } else if (type === 'event') {
                    const eventId = row.dataset.eventId;
                    data = state.events.find(e => (e._id || e.id) === eventId);
                } else if (type === 'execution') {
                    const executionId = row.dataset.executionId;
                    data = state.executions.find(e => e.id === executionId);
                } else if (type === 'request') {
                    const requestId = row.dataset.requestId;
                    data = state.requests.find(r => (r._id || r.id) === requestId);
                } else if (type === 'support') {
                    const supportId = row.dataset.supportId;
                    data = state.supportContacts.find(c => c.id === supportId);
                } else if (type === 'error') {
                    const errorId = row.dataset.errorId;
                    data = state.errors.find(e => e.id === errorId);
                }

                if (data) {
                    createDetailsPanel(type, data);
                }
            });
        });

        // Gestionnaire pour les boutons de suppression de demandes
        content.querySelectorAll('.delete-request-btn').forEach((btn) => {
            btn.addEventListener('click', async (e) => {
                e.stopPropagation(); // Empêcher l'ouverture des détails
                const requestId = btn.dataset.requestId;
                await deleteRequest(requestId, btn);
            });
        });

        // Gestionnaire pour les boutons de visualisation de tickets support
        content.querySelectorAll('.view-support-btn').forEach((btn) => {
            btn.addEventListener('click', async (e) => {
                e.stopPropagation(); // Empêcher l'ouverture des détails via le clic sur la ligne
                const supportId = btn.dataset.supportId;
                const contact = state.supportContacts.find(c => c.id === supportId);
                if (contact) {
                    createDetailsPanel('support', contact);
                }
            });
        });

        // Gestionnaire pour les boutons de clôture de tickets support
        content.querySelectorAll('.close-support-btn').forEach((btn) => {
            btn.addEventListener('click', async (e) => {
                e.stopPropagation(); // Empêcher l'ouverture des détails
                const supportId = btn.dataset.supportId;
                await closeSupportContact(supportId, btn);
            });
        });

        // Gestionnaire pour les boutons de suppression de tickets support
        content.querySelectorAll('.delete-support-btn').forEach((btn) => {
            btn.addEventListener('click', async (e) => {
                e.stopPropagation(); // Empêcher l'ouverture des détails
                const supportId = btn.dataset.supportId;
                await deleteSupportContact(supportId, btn);
            });
        });

        // Recherche d'utilisateurs : le champ vit dans .ad-toolbar depuis le redesign
        // (l'ancien wrapper .admin-users-filters n'est plus rendu).
        const usersSearchInput = content.querySelector('#admin-users-search');
        if (usersSearchInput) {
            usersSearchInput.addEventListener('input', (event) => {
                // On garde la saisie dans l'état tout de suite pour qu'un render intermédiaire ne l'efface pas
                state.usersSearch = event.target.value.trim();

                if (usersSearchTimeout) {
                    clearTimeout(usersSearchTimeout);
                }

                // Débounce: attendre 300ms après la dernière frappe
                usersSearchTimeout = setTimeout(() => {
                    state.usersPage = 1;
                    loadUsers({ page: 1, showSpinner: true });
                }, 300);
            });
        }

        // Bouton créer un utilisateur
        const createUserBtn = content.querySelector('.admin-create-user');
        if (createUserBtn) {
            createUserBtn.addEventListener('click', () => {
                showCreateUserModal();
            });
        }

        // Pagination : rendue par renderPagination() dans .scenario-pagination-modern[data-pagination].
        // On cible l'attribut data-pagination plutôt qu'une classe de présentation.
        content.querySelectorAll('[data-pagination]').forEach((pagination) => {
            const list = paginatedLists[pagination.dataset.pagination];
            if (!list) return;
            const goToPage = (delta) => {
                const totalPages = Math.max(1, state[list.totalPagesKey] || 1);
                const target = state[list.pageKey] + delta;
                if (target < 1 || target > totalPages) return;
                state[list.pageKey] = target;
                list.load({ page: target, showSpinner: true });
            };
            pagination.querySelector('.pagination-prev')?.addEventListener('click', () => goToPage(-1));
            pagination.querySelector('.pagination-next')?.addEventListener('click', () => goToPage(1));
        });

        // Bindings pour les erreurs
        // Gestion des filter pills pour les erreurs
        content.querySelectorAll('[data-filter="errors-source"]').forEach((pill) => {
            pill.addEventListener('click', () => {
                const filterSource = pill.dataset.value || '';
                state.errorFilterSource = filterSource;
                // Mettre à jour l'état actif
                content.querySelectorAll('[data-filter="errors-source"]').forEach((p) => {
                    p.classList.remove('active');
                });
                pill.classList.add('active');
                state.errorsPage = 1;
                loadErrors({ page: 1, showSpinner: true });
            });
        });

        content.querySelectorAll('[data-filter="errors-level"]').forEach((pill) => {
            pill.addEventListener('click', () => {
                const filterLevel = pill.dataset.value || '';
                state.errorFilterLevel = filterLevel;
                // Mettre à jour l'état actif
                content.querySelectorAll('[data-filter="errors-level"]').forEach((p) => {
                    p.classList.remove('active');
                });
                pill.classList.add('active');
                state.errorsPage = 1;
                loadErrors({ page: 1, showSpinner: true });
            });
        });

        content.querySelectorAll('[data-filter="errors-resolved"]').forEach((pill) => {
            pill.addEventListener('click', () => {
                const filterResolved = pill.dataset.value || '';
                state.errorFilterResolved = filterResolved;
                // Mettre à jour l'état actif
                content.querySelectorAll('[data-filter="errors-resolved"]').forEach((p) => {
                    p.classList.remove('active');
                });
                pill.classList.add('active');
                state.errorsPage = 1;
                loadErrors({ page: 1, showSpinner: true });
            });
        });

        const errorSearchInput = content.querySelector('#admin-error-search');
        if (errorSearchInput) {
            errorSearchInput.addEventListener('input', (event) => {
                state.errorSearch = event.target.value.trim();

                if (errorSearchTimeout) {
                    clearTimeout(errorSearchTimeout);
                }

                errorSearchTimeout = setTimeout(() => {
                    state.errorsPage = 1;
                    loadErrors({ page: 1, showSpinner: true });
                }, 300);
            });
        }

        // Actions sur les erreurs
        // Même panneau que le clic sur la ligne (createDetailsPanel charge l'erreur complète,
        // passe apiFetch et tient state.detailsPanel à jour).
        content.querySelectorAll('.admin-error-view').forEach((btn) => {
            btn.addEventListener('click', (e) => {
                e.stopPropagation();
                const errorId = btn.dataset.errorId;
                const data = state.errors.find((err) => err.id === errorId) || { id: errorId };
                createDetailsPanel('error', data);
            });
        });

        content.querySelectorAll('.admin-error-resolve').forEach((btn) => {
            btn.addEventListener('click', async () => {
                const errorId = btn.dataset.errorId;
                if (confirm('Marquer cette erreur comme résolue ?')) {
                    await resolveError(errorId);
                    render();
                }
            });
        });
    }

    function formatDate(value) {
        if (!value) return '—';
        try {
            return new Date(value).toLocaleString('fr-FR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
            });
        } catch {
            return value;
        }
    }

    function renderExecutionsChart(data) {
        if (!Array.isArray(data) || data.length === 0) {
            return '<p class="admin-chart-empty">Aucune donnée</p>';
        }

        const maxValue = Math.max(
            ...data.map((d) => Math.max(d.success || 0, d.error || 0, d.total || 0)),
            1
        );
        const width = 300;
        const height = 80;
        const padding = 10;
        const chartWidth = width - padding * 2;
        const chartHeight = height - padding * 2;
        const pointCount = data.length;
        const stepX = chartWidth / (pointCount - 1 || 1);

        // Ligne des succès (vert)
        const successPoints = data
            .map((d, i) => {
                const x = padding + i * stepX;
                const y = padding + chartHeight - ((d.success || 0) / maxValue) * chartHeight;
                return `${x},${y}`;
            })
            .join(' ');

        // Ligne des erreurs (rouge)
        const errorPoints = data
            .map((d, i) => {
                const x = padding + i * stepX;
                const y = padding + chartHeight - ((d.error || 0) / maxValue) * chartHeight;
                return `${x},${y}`;
            })
            .join(' ');

        return `
            <svg class="admin-chart-svg" viewBox="0 0 ${width} ${height}" preserveAspectRatio="none">
                <polyline
                    points="${successPoints}"
                    fill="none"
                    stroke="#389798"
                    stroke-width="2"
                    vector-effect="non-scaling-stroke"
                />
                <polyline
                    points="${errorPoints}"
                    fill="none"
                    stroke="#c9453a"
                    stroke-width="2"
                    vector-effect="non-scaling-stroke"
                />
            </svg>
        `;
    }

    function renderEventsChart(data) {
        if (!Array.isArray(data) || data.length === 0) {
            return '<p class="admin-chart-empty">Aucune donnée</p>';
        }

        const maxValue = Math.max(...data.map((d) => d.count || 0), 1);
        const width = 300;
        const height = 80;
        const padding = 10;
        const chartWidth = width - padding * 2;
        const chartHeight = height - padding * 2;
        const pointCount = data.length;
        const stepX = chartWidth / (pointCount - 1 || 1);

        const points = data
            .map((d, i) => {
                const x = padding + i * stepX;
                const y = padding + chartHeight - ((d.count || 0) / maxValue) * chartHeight;
                return `${x},${y}`;
            })
            .join(' ');

        return `
            <svg class="admin-chart-svg" viewBox="0 0 ${width} ${height}" preserveAspectRatio="none">
                <polyline
                    points="${points}"
                    fill="none"
                    stroke="#29768e"
                    stroke-width="2"
                    vector-effect="non-scaling-stroke"
                />
            </svg>
        `;
    }

    function renderUsersChart(data) {
        if (!Array.isArray(data) || data.length === 0) {
            return '<p class="admin-chart-empty">Aucune donnée</p>';
        }

        const maxValue = Math.max(...data.map((d) => d.count || 0), 1);
        const width = 300;
        const height = 80;
        const padding = 10;
        const chartWidth = width - padding * 2;
        const chartHeight = height - padding * 2;
        const pointCount = data.length;
        const stepX = chartWidth / (pointCount - 1 || 1);

        const points = data
            .map((d, i) => {
                const x = padding + i * stepX;
                const y = padding + chartHeight - ((d.count || 0) / maxValue) * chartHeight;
                return `${x},${y}`;
            })
            .join(' ');

        return `
            <svg class="admin-chart-svg" viewBox="0 0 ${width} ${height}" preserveAspectRatio="none">
                <polyline
                    points="${points}"
                    fill="none"
                    stroke="#151e36"
                    stroke-width="2"
                    vector-effect="non-scaling-stroke"
                />
            </svg>
        `;
    }

    function formatDateTime(value) {
        if (!value) return '—';
        try {
            return new Date(value).toLocaleString('fr-FR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
            });
        } catch {
            return value;
        }
    }

    function formatLastLogin(value) {
        if (!value) return '<span class="ad-muted">Jamais</span>';
        try {
            const loginDate = new Date(value);
            const now = new Date();
            const diffMs = now - loginDate;
            const diffMins = Math.floor(diffMs / 60000);
            const diffHours = Math.floor(diffMs / 3600000);
            const diffDays = Math.floor(diffMs / 86400000);

            if (diffMins < 1) {
                return '<span class="ad-recent">À l\'instant</span>';
            } else if (diffMins < 60) {
                return `<span class="ad-recent">Il y a ${diffMins} min</span>`;
            } else if (diffHours < 24) {
                return `<span class="ad-recent">Il y a ${diffHours} h</span>`;
            } else if (diffDays === 1) {
                return '<span>Hier</span>';
            } else if (diffDays < 7) {
                return `<span>Il y a ${diffDays} jours</span>`;
            } else {
                const day = String(loginDate.getDate()).padStart(2, '0');
                const month = String(loginDate.getMonth() + 1).padStart(2, '0');
                const hours = String(loginDate.getHours()).padStart(2, '0');
                const mins = String(loginDate.getMinutes()).padStart(2, '0');
                return `<span>${day}/${month} ${hours}:${mins}</span>`;
            }
        } catch {
            return formatDateTime(value);
        }
    }

    function renderErrorsTab() {
        if (state.errorsLoading && state.errors.length === 0) {
            return `
                <section class="admin-section">
                    <p class="admin-empty">Chargement des erreurs...</p>
                </section>
            `;
        }

        if (state.errorsError) {
            return `
                <section class="admin-section">
                    <div class="admin-error">
                        <p>${escapeHtml(state.errorsError)}</p>
                        <button class="btn-secondary admin-refresh" data-target="errors">Réessayer</button>
                    </div>
                </section>
            `;
        }

        const errors = state.errors || [];

        const tableMarkup =
            errors.length === 0
                ? renderAdminEmptyState('errors')
                : `
                    <div class="admin-table-wrapper-modern admin-errors-table-wrapper">
                        <table class="admin-table-modern">
                            <thead>
                                <tr>
                                    <th>Niveau</th>
                                    <th>Message</th>
                                    <th>Utilisateur</th>
                                    <th title="Occurrences">Nb</th>
                                    <th>Date</th>
                                    <th>Statut</th>
                                    <th><span class="ad-sr-only">Actions</span></th>
                                </tr>
                            </thead>
                            <tbody>
                                ${errors.map((error) => renderErrorRow(error)).join('')}
                            </tbody>
                        </table>
                    </div>
            `;

        const sourceFilters = [
            { key: '', label: 'Toutes les sources' },
            { key: 'frontend', label: 'App' },
            { key: 'backend', label: 'API' },
            { key: 'electron', label: 'Electron' },
        ];

        const levelFilters = [
            { key: '', label: 'Tous les niveaux' },
            { key: 'error', label: 'Erreur' },
            { key: 'warn', label: 'Avertissement' },
            { key: 'info', label: 'Info' },
        ];

        const resolvedFilters = [
            { key: '', label: 'Tous' },
            { key: 'false', label: 'Non résolues' },
            { key: 'true', label: 'Résolues' },
        ];

        return `
            <section class="admin-section">
                <div class="ad-toolbar ad-toolbar--stack">
                    <div class="ad-filter-row">
                        <div class="ad-filter">
                            <span class="ad-filter-label">Source</span>
                            <div class="admin-filter-pills">
                                ${sourceFilters.map(filter => `
                                    <button 
                                        class="admin-filter-pill ${state.errorFilterSource === filter.key ? 'active' : ''}" 
                                        data-filter="errors-source"
                                        data-value="${filter.key}"
                                        type="button"
                                    >
                                        ${escapeHtml(filter.label)}
                                    </button>
                                `).join('')}
                            </div>
                        </div>
                        <div class="ad-filter">
                            <span class="ad-filter-label">Niveau</span>
                            <div class="admin-filter-pills">
                                ${levelFilters.map(filter => `
                                    <button 
                                        class="admin-filter-pill ${state.errorFilterLevel === filter.key ? 'active' : ''}" 
                                        data-filter="errors-level"
                                        data-value="${filter.key}"
                                        type="button"
                                    >
                                        ${escapeHtml(filter.label)}
                                    </button>
                                `).join('')}
                            </div>
                        </div>
                        <div class="ad-filter">
                            <span class="ad-filter-label">Statut</span>
                            <div class="admin-filter-pills">
                                ${resolvedFilters.map(filter => `
                                    <button 
                                        class="admin-filter-pill ${state.errorFilterResolved === filter.key ? 'active' : ''}" 
                                        data-filter="errors-resolved"
                                        data-value="${filter.key}"
                                        type="button"
                                    >
                                        ${escapeHtml(filter.label)}
                                    </button>
                                `).join('')}
                            </div>
                        </div>
                    </div>
                    <div class="admin-filter-group">
                        <label for="admin-error-search">Recherche</label>
                        <div class="cmd-center-search-wrapper">
                            <svg class="cmd-center-search-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                <circle cx="11" cy="11" r="8"></circle>
                                <path d="m21 21-4.35-4.35"></path>
                            </svg>
                            <input 
                                id="admin-error-search" 
                                type="text" 
                                class="cmd-center-search-input"
                                placeholder="Message, composant..." 
                                value="${escapeHtml(state.errorSearch || '')}"
                            />
                        </div>
                    </div>
                </div>
                ${tableMarkup}
                ${renderPagination('errors', state.errorsPage, state.errorsTotalPages, state.errorsTotal, state.errorsPageSize)}
            </section>
        `;
    }

    function renderErrorRow(error) {
        const levelLabels = { error: 'Erreur', warn: 'Avertissement', info: 'Info' };
        const sourceLabels = { frontend: 'App', backend: 'API', electron: 'Electron' };

        const userInfo = error.userEmail
            ? `${escapeHtml(error.userEmail)}${error.userName ? ` (${escapeHtml(error.userName)})` : ''}`
            : error.userId
                ? `ID: ${error.userId}`
                : '—';

        const messagePreview = error.message
            ? (error.message.length > 60 ? escapeHtml(error.message.substring(0, 60)) + '...' : escapeHtml(error.message))
            : '—';

        return `
            <tr class="admin-error-row ${error.resolved ? 'admin-error-resolved' : ''} admin-table-row-clickable" data-error-id="${error.id}" data-type="error">
                <td>${pill(levelLabels[error.level] || error.level || 'Erreur', toneFor('errorLevel', error.level || 'error'))}</td>
                <td>
                    <div class="ad-stack">
                        <div class="admin-error-message ad-cell-strong" title="${escapeHtml(error.message || '')}">
                            ${messagePreview}
                        </div>
                        <span class="ad-sub">${escapeHtml(sourceLabels[error.source] || error.source || '—')}${error.component ? ` · ${escapeHtml(error.component)}` : ''}</span>
                    </div>
                </td>
                <td class="ad-cell-muted">${userInfo}</td>
                <td class="ad-cell-muted ad-cell-num">${error.occurrenceCount || 1}</td>
                <td class="ad-cell-muted ad-cell-date">${formatDateTime(error.createdAt)}</td>
                <td>${pill(error.resolved ? 'Résolue' : 'Ouverte', error.resolved ? 'success' : 'neutral')}</td>
                <td class="ad-cell-actions">
                    <div class="ad-row-actions">
                        <button class="rd-icon-btn admin-error-view" data-error-id="${error.id}" title="Voir les détails" aria-label="Voir les détails" type="button"><span class="material-symbols-outlined">visibility</span></button>
                        ${!error.resolved ? `<button class="rd-icon-btn ad-icon-success admin-error-resolve" data-error-id="${error.id}" title="Marquer comme résolu" aria-label="Marquer comme résolu" type="button"><span class="material-symbols-outlined">check</span></button>` : ''}
                    </div>
                </td>
            </tr>
        `;
    }

    // Fonction pour créer et gérer le panneau de détails
    async function createDetailsPanel(type, data) {
        // Fermer le panneau existant s'il y en a un
        closeDetailsPanel();

        // Pour les demandes, utiliser le même panel que l'utilisateur
        if (type === 'request') {
            const requestPanel = createRequestDetailsPanel({
                request: data,
                authFetch: apiFetch,
                onClose: () => {
                    state.detailsPanel = { open: false };
                },
                onRequestUpdated: () => {
                    // Recharger la liste des demandes
                    loadRequests({ page: state.requestsPage, showSpinner: false });
                },
            });
            document.body.appendChild(requestPanel);
            state.detailsPanel = { open: true, type, data };
            return; // Ne pas créer le panel admin, on utilise le panel utilisateur
        }

        // Pour le support, utiliser le même panel que l'utilisateur
        if (type === 'support') {
            // Récupérer les détails complets depuis l'API pour avoir tous les échanges
            const supportId = data?.id || data?._id;
            if (supportId) {
                try {
                    const response = await apiFetch(`/api/support/${supportId}`);
                    if (response.ok) {
                        const fullContact = await response.json();
                        const supportPanel = createSupportDetailsPanel({
                            contact: fullContact,
                            authFetch: apiFetch,
                            onClose: () => {
                                state.detailsPanel = { open: false };
                            },
                            onContactUpdated: () => {
                                // Recharger la liste des contacts support
                                if (state.currentTab === 'support') {
                                    loadSupportContacts({ page: state.supportPage, showSpinner: false });
                                }
                            },
                        });
                        document.body.appendChild(supportPanel);
                        state.detailsPanel = { open: true, type, data: fullContact };
                        return; // Ne pas créer le panel admin, on utilise le panel utilisateur
                    }
                } catch (error) {
                    log.error('Error fetching full support contact details', error);
                }
            }
            // Fallback: utiliser les données de la liste si l'API échoue
            const supportPanel = createSupportDetailsPanel({
                contact: data,
                authFetch: apiFetch,
                onClose: () => {
                    state.detailsPanel = { open: false };
                },
                onContactUpdated: () => {
                    // Recharger la liste des contacts support
                    if (state.currentTab === 'support') {
                        loadSupportContacts({ page: state.supportPage, showSpinner: false });
                    }
                },
            });
            document.body.appendChild(supportPanel);
            state.detailsPanel = { open: true, type, data };
            return; // Ne pas créer le panel admin, on utilise le panel utilisateur
        }

        // Pour les erreurs, utiliser le panel dédié
        if (type === 'error') {
            // Récupérer les détails complets depuis l'API
            const errorId = data?.id || data?._id;
            if (errorId) {
                try {
                    const fullError = await fetchJson(`/api/errors/${errorId}`);
                    const errorPanel = createErrorDetailsPanel({
                        error: fullError,
                        authFetch: apiFetch,
                        onClose: () => {
                            state.detailsPanel = { open: false };
                        },
                        onErrorUpdated: () => {
                            // Recharger la liste des erreurs
                            if (state.currentTab === 'errors') {
                                loadErrors({ page: state.errorsPage, showSpinner: false });
                            }
                        },
                    });
                    document.body.appendChild(errorPanel);
                    state.detailsPanel = { open: true, type, data: fullError };
                    return; // Ne pas créer le panel admin, on utilise le panel dédié
                } catch (error) {
                    log.error('Error fetching full error details', error);
                }
            }
            // Fallback: utiliser les données de la liste si l'API échoue
            const errorPanel = createErrorDetailsPanel({
                error: data,
                authFetch: apiFetch,
                onClose: () => {
                    state.detailsPanel = { open: false };
                },
                onErrorUpdated: () => {
                    // Recharger la liste des erreurs
                    if (state.currentTab === 'errors') {
                        loadErrors({ page: state.errorsPage, showSpinner: false });
                    }
                },
            });
            document.body.appendChild(errorPanel);
            state.detailsPanel = { open: true, type, data };
            return; // Ne pas créer le panel admin, on utilise le panel dédié
        }

        state.detailsPanel = { open: true, type, data };

        const overlay = document.createElement('div');
        overlay.className = 'admin-details-overlay';
        overlay.addEventListener('click', closeDetailsPanel);
        container.appendChild(overlay);

        const panel = document.createElement('div');
        panel.className = 'admin-details-panel';
        container.appendChild(panel);

        // Rendre le contenu selon le type
        let content = '';
        let title = '';
        let icon = '';
        let subtitle = 'Détails';

        switch (type) {
            case 'user':
                title = data.email || data.username || 'Utilisateur';
                icon = 'people';
                content = renderUserDetails(data);
                break;
            case 'event':
                title = data.type || 'Événement';
                icon = 'history';
                content = renderEventDetails(data);
                break;
            case 'execution':
                title = data.scenarioName || 'Exécution';
                icon = 'play_arrow';
                content = renderExecutionDetails(data);
                break;
        }

        panel.innerHTML = `
            <div class="admin-details-header">
                <div class="admin-details-header-top">
                    <div class="admin-details-title-wrapper">
                        <span class="material-symbols-outlined admin-details-icon">${icon}</span>
                        <div class="admin-details-title-group">
                            <h2 class="admin-details-title">${escapeHtml(title)}</h2>
                            <p class="admin-details-subtitle">${subtitle}</p>
                        </div>
                    </div>
                    <button class="admin-details-close-btn" aria-label="Fermer">
                        <span class="material-symbols-outlined">close</span>
                    </button>
                </div>
            </div>
            <div class="admin-details-content">
                ${content}
            </div>
        `;

        panel.querySelector('.admin-details-close-btn').addEventListener('click', closeDetailsPanel);

        // Animer l'ouverture
        setTimeout(() => {
            overlay.classList.add('active');
            panel.classList.add('active');
        }, 10);
    }

    function closeDetailsPanel() {
        const overlay = container.querySelector('.admin-details-overlay');
        const panel = container.querySelector('.admin-details-panel');

        if (overlay) {
            overlay.classList.remove('active');
            setTimeout(() => overlay.remove(), 300);
        }

        if (panel) {
            panel.classList.remove('active');
            setTimeout(() => panel.remove(), 300);
        }

        state.detailsPanel = { open: false, type: null, data: null };
    }

    // Fonctions de rendu des détails
    function renderUserDetails(user) {
        return `
            <div style="margin-bottom: 1.5rem;">
                <div style="padding: 1.5rem;">
                    <dl style="display: grid; gap: 1rem;">
                        <div>
                            <dt style="font-size: 0.75rem; color: #5F7380;  margin-bottom: 0.25rem;">Email</dt>
                            <dd style="font-size: 0.875rem; color: #151E36;">${escapeHtml(user.email || '—')}</dd>
                        </div>
                        <div>
                            <dt style="font-size: 0.75rem; color: #5F7380;  margin-bottom: 0.25rem;">Identifiant</dt>
                            <dd style="font-size: 0.875rem; color: #151E36;">${user.username ? `<code class="admin-user-id">${escapeHtml(user.username)}</code>` : '—'}</dd>
                        </div>
                        <div>
                            <dt style="font-size: 0.75rem; color: #5F7380;  margin-bottom: 0.25rem;">Nom</dt>
                            <dd style="font-size: 0.875rem; color: #151E36;">${escapeHtml(user.name || '—')}</dd>
                        </div>
                        <div>
                            <dt style="font-size: 0.75rem; color: #5F7380;  margin-bottom: 0.25rem;">Rôle</dt>
                            <dd style="font-size: 0.875rem; color: #151E36;">${escapeHtml(user.role || 'user')}</dd>
                        </div>
                        <div>
                            <dt style="font-size: 0.75rem; color: #5F7380;  margin-bottom: 0.25rem;">Créé le</dt>
                            <dd style="font-size: 0.875rem; color: #151E36;">${formatDate(user.createdAt)}</dd>
                        </div>
                    </dl>
                </div>
            </div>
        `;
    }

    function renderEventDetails(event) {
        return `
            <div style="margin-bottom: 1.5rem;">
                <div style="padding: 1.5rem;">
                    <dl style="display: grid; gap: 1rem;">
                        <div>
                            <dt style="font-size: 0.75rem; color: #5F7380;  margin-bottom: 0.25rem;">Type</dt>
                            <dd style="font-size: 0.875rem; color: #151E36;">${escapeHtml(event.type || '—')}</dd>
                        </div>
                        <div>
                            <dt style="font-size: 0.75rem; color: #5F7380;  margin-bottom: 0.25rem;">Description</dt>
                            <dd style="font-size: 0.875rem; color: #151E36;">${escapeHtml(event.description || '—')}</dd>
                        </div>
                        <div>
                            <dt style="font-size: 0.75rem; color: #5F7380;  margin-bottom: 0.25rem;">Utilisateur</dt>
                            <dd style="font-size: 0.875rem; color: #151E36;">${escapeHtml(event.userEmail || event.userId || '—')}</dd>
                        </div>
                        <div>
                            <dt style="font-size: 0.75rem; color: #5F7380;  margin-bottom: 0.25rem;">Cible</dt>
                            <dd style="font-size: 0.875rem; color: #151E36;">${escapeHtml(event.targetId || '—')}</dd>
                        </div>
                        <div>
                            <dt style="font-size: 0.75rem; color: #5F7380;  margin-bottom: 0.25rem;">Date</dt>
                            <dd style="font-size: 0.875rem; color: #151E36;">${formatDateTime(event.createdAt)}</dd>
                        </div>
                    </dl>
                </div>
            </div>
        `;
    }

    function renderExecutionDetails(execution) {
        const statusBadge = execution.status === 'success'
            ? '<span class="admin-badge admin-badge-success">Succès</span>'
            : execution.status === 'error'
                ? '<span class="admin-badge admin-badge-error">Erreur</span>'
                : execution.status === 'warning'
                    ? '<span class="admin-badge admin-badge-warn">Avertissement</span>'
                    : '<span class="admin-badge">' + escapeHtml(execution.status || '—') + '</span>';

        return `
            <div style="margin-bottom: 1.5rem;">
                <div style="padding: 1.5rem;">
                    <dl class="admin-error-detail-list">
                        ${execution.id || execution._id ? `
                        <dt>ID:</dt>
                        <dd><code style="background: rgba(232, 240, 242, 1); padding: 0.25rem 0.5rem; border-radius: 0.25rem; font-family: 'JetBrains Mono', monospace; font-size: 0.875rem; color: #29768E;">${escapeHtml((execution.id || execution._id).toString())}</code></dd>
                        ` : ''}
                        <dt>Scénario:</dt>
                        <dd>${escapeHtml(execution.scenarioName || '—')}</dd>
                        ${execution.scenarioId ? `
                        <dt>ID Scénario:</dt>
                        <dd><code style="background: rgba(232, 240, 242, 1); padding: 0.25rem 0.5rem; border-radius: 0.25rem; font-family: 'JetBrains Mono', monospace; font-size: 0.875rem; color: #29768E;">${escapeHtml(execution.scenarioId.toString())}</code></dd>
                        ` : ''}
                        <dt>Statut:</dt>
                        <dd>${statusBadge}</dd>
                        <dt>Utilisateur:</dt>
                        <dd>${escapeHtml(execution.userEmail || execution.userName || '—')}</dd>
                        ${execution.userId ? `
                        <dt>ID Utilisateur:</dt>
                        <dd><code style="background: rgba(232, 240, 242, 1); padding: 0.25rem 0.5rem; border-radius: 0.25rem; font-family: 'JetBrains Mono', monospace; font-size: 0.875rem; color: #29768E;">${escapeHtml(execution.userId.toString())}</code></dd>
                        ` : ''}
                        <dt>Durée:</dt>
                        <dd>${formatDurationLabel(execution.durationMs)} ${execution.durationMs ? `(${execution.durationMs}ms)` : ''}</dd>
                        <dt>Créé le:</dt>
                        <dd>${formatDateTime(execution.createdAt)}</dd>
                        ${execution.updatedAt && execution.updatedAt !== execution.createdAt ? `
                        <dt>Modifié le:</dt>
                        <dd>${formatDateTime(execution.updatedAt)}</dd>
                        ` : ''}
                        ${execution.startedAt ? `
                        <dt>Démarré le:</dt>
                        <dd>${formatDateTime(execution.startedAt)}</dd>
                        ` : ''}
                        ${execution.completedAt ? `
                        <dt>Terminé le:</dt>
                        <dd>${formatDateTime(execution.completedAt)}</dd>
                        ` : ''}
                    </dl>
                </div>
            </div>
            ${execution.errorMessage ? `
            <div style="margin-bottom: 1.5rem;">
                <div style="padding: 1.5rem;">
                    <pre class="admin-error-message-full" style="color: #9B322A;">${escapeHtml(execution.errorMessage)}</pre>
                </div>
            </div>
            ` : ''}
            ${execution.summary ? `
            <div style="margin-bottom: 1.5rem;">
                <div style="padding: 1.5rem;">
                    <p style="font-size: 0.875rem; color: #151E36; line-height: 1.6; white-space: pre-wrap;">${escapeHtml(execution.summary)}</p>
                </div>
            </div>
            ` : ''}
            ${execution.applications && execution.applications.length > 0 ? `
            <div style="margin-bottom: 1.5rem;">
                <div style="padding: 1.5rem;">
                    <ul style="list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 0.75rem;">
                        ${execution.applications.map(app => `
                            <li style="padding: 0.75rem; background: rgba(238, 241, 246, 1); border: 1px solid rgba(221, 232, 235, 1); border-radius: 0.5rem;">
                                <div style="font-weight: 600; color: #29768E; margin-bottom: 0.25rem;">${escapeHtml(app.name || app.applicationName || 'Application inconnue')}</div>
                                ${app.status ? `<div style="font-size: 0.75rem; color: #5F7380;">Statut: ${escapeHtml(app.status)}</div>` : ''}
                                ${app.error ? `<div style="font-size: 0.75rem; color: #9B322A; margin-top: 0.25rem;">Erreur: ${escapeHtml(app.error)}</div>` : ''}
                            </li>
                        `).join('')}
                    </ul>
                </div>
            </div>
            ` : ''}
            ${execution.metadata ? `
            <div style="margin-bottom: 1.5rem;">
                <div style="padding: 1.5rem;">
                    <pre class="admin-error-metadata">${escapeHtml(JSON.stringify(execution.metadata, null, 2))}</pre>
                </div>
            </div>
            ` : ''}
            ${renderExecutionLogs(execution.logs)}
        `;
    }

    // Ajouter les styles CSS pour le panel admin
    const style = document.createElement('style');
    style.id = 'admin-bio-styles';
    style.textContent = `
        /* Admin Panel Bio-luminescent Styles */
        
        .admin-tab-panel {
            margin-top: 0;
        }
        
        .admin-badge {
            display: inline-block;
            padding: 0.25rem 0.625rem;
            font-size: 0.75rem;
            font-weight: 600;
            border-radius: 0.25rem;
            text-transform: uppercase;
            letter-spacing: 0.05em;
        }
        
        .admin-badge-error {
            background: rgba(239, 68, 68, 0.2);
            color: #9B322A;
            border: 1px solid rgba(239, 68, 68, 0.3);
        }
        
        .admin-badge-success {
            background: rgba(34, 197, 94, 0.2);
            color: #2A7D55;
            border: 1px solid rgba(34, 197, 94, 0.3);
        }
        
        .admin-badge-warn {
            background: rgba(251, 191, 36, 0.2);
            color: #8B6006;
            border: 1px solid rgba(251, 191, 36, 0.3);
        }
        
        .admin-table-wrapper-modern {
            width: 100%;
            overflow-x: auto;
            border-radius: 0.5rem;
        }
        
        .admin-table-modern {
            width: 100%;
            border-collapse: collapse;
            background: transparent;
        }
        
        .admin-table-modern th {
            padding: 0.75rem 1rem;
            text-align: left;
            font-size: 0.75rem;
            font-weight: 700;
            text-transform: uppercase;
            letter-spacing: 0.1em;
            color: #389798;
            border-bottom: 1px solid rgba(221, 232, 235, 1);
            background: rgba(242, 246, 247, 1);
        }
        
        .admin-table-modern td {
            padding: 0.75rem 1rem;
            text-align: left;
            font-size: 0.875rem;
            color: #151E36;
            border-bottom: 1px solid rgba(232, 240, 242, 1);
        }
        
        /* Table erreurs plus compacte */
        .admin-errors-table-wrapper .admin-table-modern th {
            padding: 0.5rem 0.75rem;
            font-size: 0.6875rem;
        }
        
        .admin-errors-table-wrapper .admin-table-modern td {
            padding: 0.5rem 0.75rem;
            font-size: 0.75rem;
        }
        
        /* Badges plus compacts dans la table erreurs */
        .admin-errors-table-wrapper .admin-badge {
            padding: 0.125rem 0.5rem;
            font-size: 0.6875rem;
            border-radius: 0.25rem;
            font-weight: 600;
            display: inline-block;
        }
        
        /* Message plus compact */
        .admin-errors-table-wrapper .admin-error-message {
            font-size: 0.75rem;
            line-height: 1.4;
            max-width: 300px;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }
        
        /* Boutons d'action plus compacts */
        .admin-errors-table-wrapper .btn-icon {
            width: 1.5rem;
            height: 1.5rem;
            padding: 0;
            font-size: 0.875rem;
        }
        
        .admin-table-modern tr:hover td {
            background: rgba(242, 246, 247, 1);
        }
        
        .admin-table-modern tr:last-child td {
            border-bottom: none;
        }
        
        .admin-user-id {
            background: rgba(232, 240, 242, 1);
            padding: 0.25rem 0.5rem;
            border-radius: 0.25rem;
            font-family: 'JetBrains Mono', monospace;
            font-size: 0.75rem;
            color: #29768E;
            border: 1px solid #dde8eb;
        }
        
        .admin-empty {
            text-align: center;
            padding: 2rem;
            color: #5F7380;
            font-size: 0.875rem;
        }
        
        .admin-error {
            background: rgba(239, 68, 68, 0.1);
            border: 1px solid rgba(239, 68, 68, 0.3);
            border-radius: 0.5rem;
            padding: 1rem;
            color: #9B322A;
            margin-bottom: 1rem;
        }
        
        .admin-section {
            margin-bottom: 2rem;
        }
        
        /* Admin Details Panel Styles */
        .admin-details-overlay {
            position: fixed;
            inset: 0;
            background: rgba(0, 0, 0, 0.5);
            opacity: 0;
            transition: opacity 0.3s ease;
            z-index: 999;
            pointer-events: none;
        }
        
        .admin-details-overlay.active {
            opacity: 1;
            pointer-events: all;
        }
        
        .admin-details-panel {
            position: fixed;
            top: 0;
            right: 0;
            width: 100%;
            max-width: 750px;
            height: 100vh;
            background: #ffffff;
            border-left: 1px solid var(--border-primary, #dde8eb);
            box-shadow: -8px 0 24px rgba(21, 30, 54, 0.1);
            display: flex;
            flex-direction: column;
            transform: translateX(100%);
            transition: transform 0.3s ease;
            z-index: 1000;
        }
        
        .admin-details-panel.active {
            transform: translateX(0);
        }
        
        .admin-details-header {
            flex: none;
            padding: 1.5rem;
            border-bottom: 1px solid rgba(221, 232, 235, 1);
            background: rgba(238, 241, 246, 1);
        }
        
        .admin-details-header-top {
            display: flex;
            align-items: center;
            justify-content: space-between;
        }
        
        .admin-details-title-wrapper {
            display: flex;
            align-items: flex-start;
            gap: 0.75rem;
            flex: 1;
        }
        
        .admin-details-icon {
            font-size: 1.5rem;
            color: #389798;
            margin-top: 0.125rem;
            flex-shrink: 0;
        }
        
        .admin-details-title-group {
            display: flex;
            flex-direction: column;
            gap: 0.25rem;
            flex: 1;
        }
        
        .admin-details-title {
            margin: 0;
            font-size: 1.25rem;
            font-weight: 700;
            color: #29768E;
            letter-spacing: 0.05em;
        }
        
        .admin-details-subtitle {
            margin: 0;
            font-size: 0.875rem;
            font-weight: 400;
            color: #5F7380;
        }
        
        .admin-details-close-btn {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 2rem;
            height: 2rem;
            border-radius: 50%;
            background: transparent;
            border: none;
            color: #5F7380;
            cursor: pointer;
            transition: all 0.2s ease;
        }
        
        .admin-details-close-btn:hover {
            background: rgba(56, 151, 152, 0.1);
            color: #389798;
        }
        
        .admin-details-content {
            flex: 1;
            overflow-y: auto;
            padding: 1.5rem;
        }
        
        /* Table row clickable */
        .admin-table-modern tbody tr {
            cursor: pointer;
            transition: background 0.2s ease;
        }
        
        .admin-table-modern tbody tr:hover {
            background: rgba(56, 151, 152, 0.1) !important;
        }
        
        /* Panel de détails des erreurs */
        
        .admin-error-detail-list {
            display: grid;
            grid-template-columns: auto 1fr;
            gap: 0.75rem 1.5rem;
            margin: 0;
        }
        
        .admin-error-detail-list dt {
            font-weight: 600;
            color: #5F7380;
            font-size: 0.875rem;
        }
        
        .admin-error-detail-list dd {
            margin: 0;
            color: #151E36;
            font-size: 0.875rem;
            word-break: break-word;
        }
        
        .admin-error-message-full,
        .admin-error-metadata {
            background: rgba(232, 240, 242, 1);
            border: 1px solid rgba(221, 232, 235, 1);
            border-radius: 0.5rem;
            padding: 1rem;
            color: #151E36;
            font-family: 'JetBrains Mono', monospace;
            font-size: 0.8125rem;
            line-height: 1.6;
            white-space: pre-wrap;
            word-break: break-word;
            overflow-x: auto;
            max-height: 400px;
            overflow-y: auto;
        }
        
        .admin-error-metadata {
            color: #29768E;
        }
    `;
    document.head.appendChild(style);

    // Handle environment selector change
    const envSelect = container.querySelector('#admin-env-select');
    const envSelectorContainer = container.querySelector('.admin-env-selector');
    
    // Set initial badge
    if (envSelectorContainer && currentEnv !== 'auto') {
        envSelectorContainer.setAttribute('data-env', currentEnv);
    }
    
    // Listen to environment changes globally
    // This allows any component to change the environment and have the admin reload data
    window.addEventListener('launcher:env-changed', (e) => {
        const newEnv = e.detail?.environment || getCurrentEnvironment();
        log.info('Environment changed event received', { newEnv });
        
        // Update badge if environment selector exists
        if (envSelectorContainer) {
            if (newEnv === 'auto') {
                envSelectorContainer.removeAttribute('data-env');
            } else {
                envSelectorContainer.setAttribute('data-env', newEnv);
            }
        }
        
        // Update the select value if it exists
        if (envSelect && envSelect.value !== newEnv) {
            envSelect.value = newEnv;
        }
        
        // Clear all cached data
        state.overview = null;
        state.users = [];
        state.requests = [];
        state.events = [];
        state.executions = [];
        state.errors = [];
        state.supportContacts = [];
        state.emailTemplates = [];
        state.emails = [];
        
        // Reset pagination
        state.usersPage = 1;
        state.requestsPage = 1;
        state.eventsPage = 1;
        state.executionsPage = 1;
        state.errorsPage = 1;
        state.supportPage = 1;
        state.emailsPage = 1;
        
        // Reload data for current tab
        setTimeout(() => {
            // Always reload overview as it's shown in the dashboard
            loadOverview(true);
            
            // Reload data based on current tab
            switch(state.currentTab) {
                case 'overview':
                    loadData(true);
                    break;
                case 'users':
                    loadUsers({ page: 1, showSpinner: true });
                    break;
                case 'requests':
                    loadRequests({ page: 1, showSpinner: true });
                    break;
                case 'support':
                    loadSupportContacts({ page: 1, showSpinner: true });
                    break;
                case 'events':
                    loadEvents({ page: 1, showSpinner: true });
                    break;
                case 'executions':
                    loadExecutions({ page: 1, showSpinner: true });
                    break;
                case 'errors':
                    loadErrors({ page: 1, showSpinner: true });
                    break;
                case 'emails':
                    loadEmails({ page: 1, showSpinner: true });
                    break;
                case 'templates':
                    loadEmailTemplates(true);
                    break;
                default:
                    loadData(true);
            }
        }, 100);
    });
    
    // Handle environment selector change
    if (envSelect) {
        envSelect.addEventListener('change', (e) => {
            const newEnv = e.target.value;
            log.info('Environment selector changed', { newEnv });
            
            // Set the new environment (this will automatically dispatch 'launcher:env-changed')
            setApiEnvironment(newEnv);
        });
    }

    bindSidebarNav();
    bindEventsAutocompleteOutsideClick();
    loadData(true);
    loadOverview(true);
    loadEvents({ page: state.eventsPage, showSpinner: true });
    loadExecutions({ page: state.executionsPage, showSpinner: true });
    loadUsers({ page: state.usersPage, showSpinner: false });
    loadRequests({ page: state.requestsPage, showSpinner: false });
    loadErrors({ page: state.errorsPage, showSpinner: false });
    return container;
}
